import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useAuth } from '../contexts/AuthContext';
import { formatPHP, fmtDateTime, fmtDate } from '../lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';
import { Card, CardContent } from './ui/card';
import ReceiptGallery from './ReceiptGallery';
import UploadQRDialog from './UploadQRDialog';
import ViewQRDialog from './ViewQRDialog';
import VerificationBadge from './VerificationBadge';
import VerifyPinDialog from './VerifyPinDialog';
import PrintEngine from '../lib/PrintEngine';
import CustomerAccountModal from './CustomerAccountModal';
import SendToPrintModal from './SendToPrintModal';
import FixReceiptDialog from './FixReceiptDialog';
import ReceiptQRDialog from './ReceiptQRDialog';
import ProofOfPaymentDialog from './ProofOfPaymentDialog';
import SalePaymentHistoryDialog, { paymentSummaryLine } from './SalePaymentHistoryDialog';
import {
  FileText, Edit3, History, Save, X, AlertTriangle, Package,
  User, Calendar, DollarSign, Trash2, Clock, CheckCircle2,
  Copy, Check, ShieldCheck, Ban, ImageIcon, Upload, Smartphone,
  Truck, CreditCard, Wallet, Pencil, RefreshCw, Printer, MessageSquare, RotateCcw, Send,
  ChevronDown, QrCode, ArrowLeft
} from 'lucide-react';
import { formatDateTime } from '../lib/dateFormat';
import { lineDescription, docExtras } from '../lib/docExtras';
import PrintExtrasDialog from './PrintExtrasDialog';
import { toast } from 'sonner';
import CalcInput from './CalcInput';

// ── Version timeline (owner feature 2026-07-29) ──────────────────────────────
// invoice_edits records store original_state = the FULL pre-edit snapshot, so
// the receipt's life reconstructs as: 1st Encode = edit1.original_state,
// Edit 1 result = edit2.original_state, …, latest = the current invoice.
// Older edits made before original_state existed render as changes-only.
function buildVersions(invoice, editsDesc) {
  // Sort oldest-first by timestamp (robust to either input order)
  const edits = [...(editsDesc || [])].sort((a, b) => (a.edited_at || '').localeCompare(b.edited_at || ''));
  if (!edits.length) return [];
  const versions = [];
  for (let i = 0; i < edits.length; i++) {
    versions.push({
      label: i === 0 ? '1st Encode' : `Edit ${i}`,
      state: edits[i].original_state || null,
      by: i === 0 ? (invoice.cashier_name || '') : (edits[i - 1].edited_by_name || ''),
      at: i === 0 ? (invoice.created_at || '') : (edits[i - 1].edited_at || ''),
      reason: i === 0 ? '' : (edits[i - 1].reason || ''),
      changes: i === 0 ? [] : (edits[i - 1].changes || []),
    });
  }
  const last = edits[edits.length - 1];
  versions.push({
    label: `Edit ${edits.length} (current)`,
    state: { items: invoice.items || [], subtotal: invoice.subtotal, grand_total: invoice.grand_total },
    by: last.edited_by_name || '',
    at: last.edited_at || '',
    reason: last.reason || '',
    changes: last.changes || [],
  });
  return versions;
}

// Per-line diff vs the previous version: added lines, qty/price changes, removals.
function diffVersionItems(prevState, state) {
  const prevItems = (prevState && prevState.items) || [];
  const curItems = (state && state.items) || [];
  const prev = new Map(prevItems.map(i => [i.product_id, i]));
  const marks = {};
  for (const it of curItems) {
    const p = prev.get(it.product_id);
    if (!p) { marks[it.product_id] = { added: true }; continue; }
    const m = {};
    if (parseFloat(p.quantity || 0) !== parseFloat(it.quantity || 0)) m.qty = [p.quantity, it.quantity];
    const pr = parseFloat(p.rate ?? p.price ?? 0), cr = parseFloat(it.rate ?? it.price ?? 0);
    if (pr !== cr) m.rate = [pr, cr];
    if (Object.keys(m).length) marks[it.product_id] = m;
  }
  const removed = prevItems.filter(i => !curItems.some(n => n.product_id === i.product_id));
  return { marks, removed };
}

// Shared Versions + Print-trail view — rendered by BOTH modal layouts (full
// section tab and compact inline block) so the story is identical everywhere.
function VersionsAndPrints({ invoice, editsDesc, printHistory }) {
  const versions = buildVersions(invoice, editsDesc);
  return (
    <div className="space-y-5">
      <div className="space-y-3">
        <h3 className="font-semibold text-sm flex items-center gap-2"><History size={16} /> Versions</h3>
        {versions.length === 0 ? (
          <p className="text-slate-400 text-sm text-center py-4">
            Never edited — the receipt is still its original encode.
          </p>
        ) : (
          <div className="space-y-3" data-testid="version-timeline">
            {versions.map((v, vi) => {
              const prev = vi > 0 ? versions[vi - 1].state : null;
              const { marks, removed } = vi > 0 ? diffVersionItems(prev, v.state) : { marks: {}, removed: [] };
              return (
                <Card key={vi} className={vi === versions.length - 1 ? 'border-emerald-300' : 'border-slate-200'} data-testid={`version-${vi}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className={`text-[10px] ${vi === 0 ? 'bg-blue-100 text-blue-700' : vi === versions.length - 1 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'} border-0`}>
                          {v.label}
                        </Badge>
                        <span className="text-xs text-slate-400">
                          {v.by}{v.at ? ` · ${formatDateTime(v.at)}` : ''}
                        </span>
                      </div>
                      {v.state?.grand_total != null && (
                        <span className="text-sm font-bold" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(v.state.grand_total)}</span>
                      )}
                    </div>
                    {v.reason && (
                      <p className="text-xs text-slate-500 mb-2"><span className="font-medium text-slate-600">Reason:</span> {v.reason}</p>
                    )}
                    {v.state?.items ? (
                      <div className="rounded-lg border border-slate-100 overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left px-2 py-1.5 font-semibold text-slate-500">Item</th>
                              <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Qty</th>
                              <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Price</th>
                              <th className="text-right px-2 py-1.5 font-semibold text-slate-500">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {v.state.items.map((it, ii) => {
                              const m = marks[it.product_id] || {};
                              const rowCls = m.added ? 'bg-emerald-50' : (m.qty || m.rate) ? 'bg-amber-50' : '';
                              return (
                                <tr key={ii} className={`border-t border-slate-100 ${rowCls}`}>
                                  <td className="px-2 py-1.5">
                                    {it.product_name}
                                    {m.added && <span className="ml-1 text-[9px] font-bold text-emerald-700">ADDED</span>}
                                  </td>
                                  <td className="px-2 py-1.5 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {m.qty && <span className="text-[9px] text-amber-700 line-through mr-1">{m.qty[0]}</span>}
                                    {it.quantity}{it.unit ? ` ${it.unit}` : ''}
                                  </td>
                                  <td className="px-2 py-1.5 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>
                                    {m.rate && <span className="text-[9px] text-amber-700 line-through mr-1">{formatPHP(m.rate[0])}</span>}
                                    {formatPHP(it.rate ?? it.price ?? 0)}
                                  </td>
                                  <td className="px-2 py-1.5 text-right font-medium" style={{ fontVariantNumeric: 'tabular-nums' }}>{formatPHP(it.total ?? 0)}</td>
                                </tr>
                              );
                            })}
                            {removed.map((it, ri) => (
                              <tr key={`r${ri}`} className="border-t border-slate-100 bg-red-50 opacity-70">
                                <td className="px-2 py-1.5 line-through text-red-700">{it.product_name}<span className="ml-1 text-[9px] font-bold no-underline">REMOVED</span></td>
                                <td className="px-2 py-1.5 text-right line-through text-red-700">{it.quantity}</td>
                                <td className="px-2 py-1.5 text-right line-through text-red-700">{formatPHP(it.rate ?? it.price ?? 0)}</td>
                                <td className="px-2 py-1.5 text-right line-through text-red-700">{formatPHP(it.total ?? 0)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      v.changes.length > 0 && (
                        <ul className="text-xs text-slate-600 space-y-0.5">
                          {v.changes.map((c, j) => <li key={j} className="flex items-start gap-1"><span className="text-slate-400">•</span> {c}</li>)}
                        </ul>
                      )
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <h3 className="font-semibold text-sm flex items-center gap-2">
          <Printer size={15} /> Print History
          {printHistory?.events?.length > 0 && (
            <Badge className="text-[10px] bg-slate-100 text-slate-600 border-0">{printHistory.events.length} print{printHistory.events.length === 1 ? '' : 's'} recorded</Badge>
          )}
        </h3>
        {!printHistory || printHistory.events?.length === 0 ? (
          <p className="text-slate-400 text-xs">
            No prints recorded yet. Prints are logged from the moment this feature went live — older paper copies predate the log.
          </p>
        ) : (
          <div className="rounded-lg border border-slate-100 divide-y divide-slate-50" data-testid="print-history-list">
            {printHistory.events.map((ev, i) => (
              <div key={ev.id || i} className="flex items-center justify-between px-3 py-1.5 text-xs">
                <span className="text-slate-600">
                  {formatDateTime(ev.at)}
                  {ev.by ? ` · ${ev.by}` : ev.device_id ? ' · terminal QR' : ''}
                </span>
                <span className="text-slate-400">
                  {ev.format ? ev.format.replace('_', ' ') : ev.doc_type}
                  {ev.copy_number ? ` · copy #${ev.copy_number}` : ''}
                  {ev.source ? ` · from ${ev.source}` : ''}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const LAST_PRINT_FORMAT_KEY = 'agripos_last_print_format';
// Full Page was retired in Jul 2026 — print() has silently rendered the
// dot-matrix layout for it ever since. It stays in this map ONLY so a device
// that saved "full_page" as its last format still shows a truthful label
// (Dot Matrix, which is what the paper is); it is no longer offered as a
// choice below.
const PRINT_FORMATS = { thermal: 'Thermal', dot_matrix: 'Dot Matrix', full_page: 'Dot Matrix' };

export default function InvoiceDetailModal({
  open, onOpenChange, invoiceId, invoiceNumber, expenseId, onUpdated,
  saleId, compact = false
}) {
  // saleId is a backward-compat alias for invoiceId (Phase 2 consolidation)
  // A receipt clicked inside a Proof of Payment SWAPS this window to that
  // receipt (no third window on top) and leaves a "Back" trail
  // (owner 2026-09-22). `hopStack` holds where we came from.
  const [hopId, setHopId] = useState(null);
  const [hopStack, setHopStack] = useState([]);   // [{ id, number }]
  const resolvedInvoiceId = hopId || invoiceId || saleId;

  const { hasPerm, user, currentBranch } = useAuth();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  const [printHistory, setPrintHistory] = useState(null);
  const [amendable, setAmendable] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({});

  // ── Capital view on Sale Detail (owner feature 2026-08-26) ─────────────────
  // Same policy as /sales-new: allowed roles fetch with no PIN (backend
  // self-authorizes), everyone else gets the PIN prompt (`view_capital_costs`).
  // Costs come from the bulk cost-details endpoint scoped to the SALE's branch,
  // so a receipt viewed from another branch still shows the branch it was sold at.
  const [capShown, setCapShown] = useState(false);
  const [capCosts, setCapCosts] = useState({});
  const [capPinOpen, setCapPinOpen] = useState(false);
  const [capPinInput, setCapPinInput] = useState('');
  const [capBusy, setCapBusy] = useState(false);

  const fetchCapital = async (pin) => {
    const ids = [...new Set((invoice?.items || []).map(it => it.product_id).filter(Boolean))];
    if (!ids.length) { toast.error('No products on this receipt'); return; }
    setCapBusy(true);
    try {
      const body = { branch_id: invoice?.branch_id || currentBranch?.id, product_ids: ids };
      if (pin) body.pin = pin;
      const r = await api.post('/products/cost-details', body);
      setCapCosts(r.data?.costs || {});
      setCapShown(true);
      setCapPinOpen(false);
      setCapPinInput('');
    } catch (e) {
      if (e.response?.status === 403 && !pin) setCapPinOpen(true);
      else toast.error(e.response?.data?.detail || 'Could not load capital');
    }
    setCapBusy(false);
  };

  const toggleCapital = () => {
    if (capShown) { setCapShown(false); setCapCosts({}); return; }
    fetchCapital();
  };

  // Per-item capital refs line — red MA / amber LP, underlined when the sale
  // rate is at/below that cost (mirrors the /sales-new line indicators).
  // The line's own description — typed on /sales-new (detailed mode) or filled
  // in by the product. It was only ever rendered as a FALLBACK for a missing
  // product name, so the lines that need it most never showed it: a Farm
  // Service line is named "Farm Service:" and the description ("CA HAWLER",
  // "pamasahi") is the only record of what was actually sold. One renderer for
  // both layouts so the compact and wide views cannot drift apart.
  const lineDescriptionFor = (item) => {
    const desc = lineDescription(item);
    if (!desc) return null;
    return (
      <p className="text-[11px] text-slate-500 mt-0.5 whitespace-pre-wrap" data-testid="line-description">
        {desc}
      </p>
    );
  };

  const capitalRefsFor = (item) => {
    if (!capShown || !item?.product_id) return null;
    const c = capCosts[item.product_id];
    if (!c) return null;
    const rate = parseFloat(item.rate ?? item.unit_price ?? item.price ?? 0) || 0;
    const eff = parseFloat(c.effective_cost) || 0;
    const ma = parseFloat(c.moving_average) || 0;
    const lp = parseFloat(c.last_purchase) || 0;
    return (
      <p className="text-[11px] mt-0.5" data-testid="capital-refs">
        <span className="text-slate-500">Cap {formatPHP(eff)}</span>
        {ma > 0 && (
          <span className={`ml-1.5 ${rate > 0 && rate <= ma ? 'text-red-700 font-bold underline decoration-red-700' : 'text-red-500 font-semibold'}`}>
            · MA {formatPHP(ma)}
          </span>
        )}
        {lp > 0 && (
          <span className={`ml-1.5 ${rate > 0 && rate <= lp ? 'text-amber-700 font-bold underline decoration-amber-700' : 'text-amber-600 font-semibold'}`}>
            · LP {formatPHP(lp)}
          </span>
        )}
      </p>
    );
  };

  // Re-lock capital whenever the modal closes or a different receipt opens —
  // mirrors the sales screen's "re-PIN on reopen" stance for margin data.
  useEffect(() => {
    setCapShown(false); setCapCosts({}); setCapPinOpen(false); setCapPinInput('');
  }, [resolvedInvoiceId, open]);

  const capitalToggleBtn = (
    <button onClick={toggleCapital} disabled={capBusy}
      className="text-[11px] underline text-slate-400 hover:text-blue-600 disabled:opacity-50"
      data-testid="detail-show-capital-btn">
      {capBusy ? 'Loading…' : capShown ? 'Hide Capital' : 'Show Capital'}
    </button>
  );

  // Edit form state
  const [editData, setEditData] = useState({ items: [], customer_name: '', notes: '', freight: 0, overall_discount: 0 });
  const [editReason, setEditReason] = useState('');
  const [proofFile, setProofFile] = useState(null);

  // Action dialogs
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voidPin, setVoidPin] = useState('');
  const [reopenAfterVoid, setReopenAfterVoid] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyPin, setVerifyPin] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Compact-mode edit state
  const [editOrderDate, setEditOrderDate] = useState('');
  const [editDateClosed, setEditDateClosed] = useState(false);

  // External verify dialog (compact mode)
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);

  // QR dialogs
  const [uploadQROpen, setUploadQROpen] = useState(false);
  const [viewQROpen, setViewQROpen] = useState(false);
  // The CUSTOMER's digital receipt (/doc/{code}) as a QR + copy-link — owner
  // 2026-09-21: a customer who lost the paper asks for a copy; staff were
  // re-printing just to scan the QR. ("Phone" above is a different thing:
  // the uploaded receipt PHOTOS, for staff, on a 1-hour link.)
  const [receiptQROpen, setReceiptQROpen] = useState(false);
  const [photosVersion, setPhotosVersion] = useState(0);   // bumped after an upload → the photos panel reloads
  // Payment history (owner 2026-09-22): a one-line summary + link in the window,
  // the list itself in its own dialog, each PP a link to the shared PP window.
  const [payHistoryOpen, setPayHistoryOpen] = useState(false);
  const [ppOpenNumber, setPpOpenNumber] = useState('');
  const hopToReceipt = (line) => {
    if (!line?.invoice_id || !invoice?.id) return;
    // remember the PP we came THROUGH, not only the receipt we came FROM: Back
    // reopens that PP so every receipt it settled can be reviewed in turn
    // (owner 2026-09-22: "it loses its purpose if the PP as a whole disappears").
    setHopStack(prev => [...prev, { id: invoice.id, number: invoice.invoice_number || invoice.sale_number || '', pp: ppOpenNumber }]);
    setPpOpenNumber('');
    setPayHistoryOpen(false);
    setHopId(line.invoice_id);
  };
  const hopBack = () => {
    const from = hopStack[hopStack.length - 1];
    setHopStack(prev => prev.slice(0, -1));
    // back to the receipt we came from; the very first one is the caller's own
    setHopId(from && from.id !== (invoiceId || saleId) ? from.id : null);
    if (from?.pp) setPpOpenNumber(from.pp);   // …with the Proof of Payment open again
  };
  // While on a hopped receipt, clicking outside it or pressing Esc goes BACK to
  // the PP instead of closing everything; the X still closes everything.
  const hopBackInsteadOfClose = (e, otherDialogOpen) => {
    if (otherDialogOpen) { e.preventDefault(); return; }
    if (hopStack.length > 0) { e.preventDefault(); hopBack(); }
  };
  // One Print button instead of three: it prints with the format THIS device
  // used last (a thermal counter stays one tap), the caret holds the rest.
  const [lastPrintFormat, setLastPrintFormat] = useState(() => {
    try {
      const f = window.localStorage.getItem(LAST_PRINT_FORMAT_KEY);
      return PRINT_FORMATS[f] ? f : 'thermal';
    } catch { return 'thermal'; }
  });
  // Print-extras prompt. A sale carrying a line description or a note asks
  // what to include BEFORE it prints — the receipt goes to the customer, and an
  // internal note is not always theirs to read. A plain sale never sees it, so
  // the counter is not slowed down for nothing. No "remember" option by owner's
  // ruling (2026-09-23): "I may print it now, but not later."
  const [printExtrasFormat, setPrintExtrasFormat] = useState(null);
  const printAs = (format) => {
    setLastPrintFormat(format);
    try { window.localStorage.setItem(LAST_PRINT_FORMAT_KEY, format); } catch { /* private mode */ }
    if (docExtras(invoice).hasAny) { setPrintExtrasFormat(format); return; }
    handlePrint(format);
  };

  // Send to Print
  const [sendToPrintOpen, setSendToPrintOpen] = useState(false);
  const [packingSendOpen, setPackingSendOpen] = useState(false);

  // Quick freight edit
  const [freightEditOpen, setFreightEditOpen] = useState(false);
  const [freightInput, setFreightInput] = useState('');
  const [freightReason, setFreightReason] = useState('');
  const [freightPin, setFreightPin] = useState('');
  const [freightSaving, setFreightSaving] = useState(false);
  const [freightError, setFreightError] = useState('');
  // Freight history
  const [freightHistory, setFreightHistory] = useState([]);
  const [freightHistoryLoaded, setFreightHistoryLoaded] = useState(false);

  // Active section
  const [section, setSection] = useState('detail'); // detail | receipts | payments | history | signature

  // Signature state
  const [signatures, setSignatures] = useState([]);
  const [sigLoading, setSigLoading] = useState(false);

  // SMS state
  const [smsItems, setSmsItems] = useState([]);
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsDiagnosis, setSmsDiagnosis] = useState(null);

  // Load business info for printing — always needed, not just in compact mode
  useEffect(() => {
    api.get('/settings/business-info').then(r => setBusinessInfo(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (open && (resolvedInvoiceId || invoiceNumber || expenseId)) {
      loadInvoice();
      setSection('detail');
      setEditMode(false);
      setShowHistory(false);
      setFreightHistoryLoaded(false);
    }
  // eslint-disable-next-line
  }, [open, resolvedInvoiceId, invoiceNumber, expenseId]);

  // Load freight history lazily when invoice data is available and not yet loaded
  useEffect(() => {
    if (invoice && !freightHistoryLoaded && open) {
      setFreightHistoryLoaded(true);
      api.get(`/invoices/${invoice.id}/freight-history`)
        .then(r => setFreightHistory(r.data.history || []))
        .catch(() => setFreightHistory([]));
    }
  }, [invoice, freightHistoryLoaded, open]);

  // Reset nested overlays on close — `if (!open) return null` doesn't unmount,
  // so QR/verify state would persist and re-show on the next open.
  useEffect(() => {
    if (!open) {
      setUploadQROpen(false);
      setViewQROpen(false);
      setVerifyDialogOpen(false);
      setPayHistoryOpen(false);
      setPpOpenNumber('');
      setHopId(null);
      setHopStack([]);
    }
  }, [open]);

  const loadInvoice = async () => {
    setLoading(true);
    try {
      let res;
      if (expenseId) {
        res = await api.get(`/expenses/${expenseId}`);
        res.data._collection = 'expenses';
      } else if (resolvedInvoiceId) {
        res = await api.get(`/invoices/${resolvedInvoiceId}`);
      } else {
        res = await api.get(`/invoices/by-number/${encodeURIComponent(invoiceNumber)}`);
      }
      setInvoice(res.data);
      setEditData({
        // Backfill per-unit discount_value on legacy lines that only stored the
        // TOTAL discount_amount — otherwise saving an edit recomputes the
        // discount from a missing discount_value and silently wipes it.
        // (Same fallback as UnifiedSalesPage reopen-as-sale.)
        items: JSON.parse(JSON.stringify(res.data.items || [])).map((it) => {
          const dv = parseFloat(it.discount_value);
          const da = parseFloat(it.discount_amount) || 0;
          const qty = parseFloat(it.quantity) || 0;
          const rate = parseFloat(it.rate || it.unit_price || it.price) || 0;
          if ((!dv || isNaN(dv)) && da > 0 && qty > 0) {
            if ((it.discount_type || 'amount') === 'percent' && rate > 0) {
              it.discount_value = Math.round((da / (qty * rate)) * 1000000) / 10000;
            } else {
              it.discount_type = 'amount';
              it.discount_value = Math.round((da / qty) * 10000) / 10000;
            }
          }
          return it;
        }),
        customer_name: res.data.customer_name || '',
        notes: res.data.notes || res.data.description || '',
        freight: res.data.freight || 0,
        overall_discount: res.data.overall_discount || 0,
      });
      setEditHistory(res.data.edit_history || []);

      // Load signatures for this invoice
      const invId = res.data.id;
      if (invId) {
        setSigLoading(true);
        api.get(`/signatures/record/invoice/${invId}`)
          .then(r => setSignatures(r.data || []))
          .catch(() => setSignatures([]))
          .finally(() => setSigLoading(false));
        // Load SMS queue items linked to this invoice (best-effort)
        setSmsLoading(true);
        api.get('/sms/queue', { params: { trigger_ref: invId, limit: 50 } })
          .then(r => setSmsItems(r.data?.items || []))
          .catch(() => setSmsItems([]))
          .finally(() => setSmsLoading(false));
      }
    } catch (e) {
      toast.error('Failed to load details');
      onOpenChange(false);
    }
    setLoading(false);
  };

  // ── Derived state ──────────────────────────────────────────────────────
  const isPO = invoice?._collection === 'purchase_orders';
  const isExpense = invoice?._collection === 'expenses';
  const isInvoice = !isPO && !isExpense;
  const docNumber = invoice?.invoice_number || invoice?.sale_number || invoice?.po_number || invoice?.reference_number || invoice?.linked_invoice_number || '';
  const docName = isPO ? (invoice?.vendor || '') : isExpense ? (invoice?.description || invoice?.category || '') : (invoice?.customer_name || 'Walk-in');
  const docDate = invoice?.order_date || invoice?.purchase_date || invoice?.date || fmtDate(invoice?.created_at) || '';
  const isVerified = invoice?.verified === true;
  const isVoided = invoice?.status === 'voided' || invoice?.status === 'cancelled';
  const payments = invoice?.payments || [];
  const recordType = isPO ? 'purchase_order' : isExpense ? 'expense' : 'invoice';
  const recordId = invoice?.id;

  // Permissions
  const isAdmin = user?.role === 'admin';
  // ── New-balance warning after an upward edit (owner 2026-08-24) ────────
  // A fully-paid receipt edited to a higher total becomes a receivable —
  // surface it loudly and offer to collect right away via the existing
  // customer-account collect flow.
  const [balanceWarn, setBalanceWarn] = useState(null);
  const [collectAcctOpen, setCollectAcctOpen] = useState(false);
  // Walk-in difference-sale offer (owner design 2026-08-24): the edit is
  // blocked server-side; we offer to sell the shortfall as a NEW receipt in
  // /sales-new — original prices AND discounts copied so promised pricing
  // is respected; stock/payment/printing all follow the normal sale law.
  const [diffOffer, setDiffOffer] = useState(null);
  // Fix Receipt (Phase 3): the one-pass correction flow. fixPrefill carries
  // an attempted edit into the Fix dialog (guided walk-in path — the fix
  // endpoint collects atomically, which the plain edit door cannot).
  const [fixOpen, setFixOpen] = useState(false);
  const [fixPrefill, setFixPrefill] = useState(null);
  // Standard PIN entry for the compact-edit refund (ruling 8 — the raw
  // browser prompt() is retired; this is the same dialog-with-input shape
  // every other PIN gate uses).
  const [refundPinAsk, setRefundPinAsk] = useState(null); // {message}
  const [refundPinInput, setRefundPinInput] = useState('');

  const buildFixPrefillFromEdit = () => {
    const orig = invoice?.items || [];
    const edited = editData?.items || [];
    const corrections = [];
    const additions = [];
    edited.forEach((it, idx) => {
      if (idx < orig.length) {
        const c = { line_index: idx };
        let changed = false;
        const q = parseFloat(it.quantity || 0), r = parseFloat(it.rate || 0);
        if (Math.abs(q - parseFloat(orig[idx]?.quantity || 0)) > 1e-9) { c.quantity = q; changed = true; }
        if (Math.abs(r - parseFloat(orig[idx]?.rate || 0)) > 0.0049) { c.rate = r; changed = true; }
        if (changed) corrections.push(c);
      } else if (it.product_id) {
        additions.push({
          product_id: it.product_id, product_name: it.product_name || '',
          quantity: parseFloat(it.quantity || 0), rate: parseFloat(it.rate || 0),
          unit: it.unit || '', is_repack: !!it.is_repack,
        });
      }
    });
    return { corrections, additions, reason: editReason };
  };

  const openFixFromEdit = () => {
    setFixPrefill(buildFixPrefillFromEdit());
    setDiffOffer(null);
    setEditMode(false);
    setFixOpen(true);
  };
  const computeDiffLines = () => {
    const orig = invoice?.items || [];
    const edited = editData?.items || [];
    const lines = [];
    edited.forEach((it, idx) => {
      const before = idx < orig.length ? parseFloat(orig[idx]?.quantity || 0) : 0;
      const after = parseFloat(it.quantity || 0);
      const delta = after - before;
      if (delta > 0.0001 && it.product_id) {
        const src = idx < orig.length ? orig[idx] : it;
        lines.push({
          product_id: it.product_id,
          product_name: it.product_name || src.product_name || '',
          unit: it.unit || src.unit || '',
          quantity: delta,
          rate: parseFloat(src.rate || it.rate || 0),
          discount_type: src.discount_type || it.discount_type || 'amount',
          discount_value: parseFloat(src.discount_value ?? it.discount_value ?? 0),
          is_repack: it.is_repack || src.is_repack || false,
        });
      }
    });
    return lines;
  };
  // "Copy this receipt" (owner 2026-08-24): prefill a brand-new sale in
  // /sales-new with this receipt's scheme, customer, lines, prices and
  // discounts — dated today. Works on voided receipts (re-encode partner
  // of void-with-BIR-receipt).
  const copyReceipt = () => {
    if (!invoice) return;
    sessionStorage.setItem('agb_copy_prefill', JSON.stringify(invoice));
    onOpenChange?.(false);
    navigate('/sales-new?copy=1');
  };

  const openDiffSale = () => {
    if (!diffOffer) return;
    sessionStorage.setItem('agb_diff_prefill', JSON.stringify({
      invoice_number: invoice?.invoice_number || '',
      invoice_id: invoice?.id || '',
      lines: diffOffer.lines,
    }));
    setDiffOffer(null);
    onOpenChange?.(false);
    navigate('/sales-new?diff=1');
  };
  const maybeWarnNewBalance = (updatedInv, prevInv) => {
    if (!updatedInv || !prevInv) return;
    const wasFullyPaid = prevInv.status === 'paid' && !(parseFloat(prevInv.balance || 0) > 0);
    const newBal = parseFloat(updatedInv.balance || 0);
    if (wasFullyPaid && newBal > 0.005) {
      setBalanceWarn({
        total: parseFloat(updatedInv.grand_total || 0),
        paid: parseFloat(updatedInv.amount_paid || 0),
        balance: newBal,
        customerId: updatedInv.customer_id || '',
        customerName: updatedInv.customer_name || '',
      });
    }
  };

  const renderBalanceWarnDialogs = () => (
    <>
      <Dialog open={!!diffOffer} onOpenChange={v => { if (!v) setDiffOffer(null); }}>
        <DialogContent className="max-w-md" data-testid="diff-sale-offer">
          <DialogHeader>
            <DialogTitle className="text-amber-700">Sell the difference as a new receipt?</DialogTitle>
          </DialogHeader>
          {diffOffer && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                This walk-in receipt is fully paid, so its quantities can't be raised.
                The extra goods can be sold on a <b>new receipt</b> instead — same prices
                and discounts as the original. You can take payment there, or attach a
                customer and charge it as credit.
              </p>
              <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-xs space-y-1">
                {diffOffer.lines.map((l, i) => (
                  <div key={i} className="flex justify-between">
                    <span>{l.product_name} × {l.quantity}</span>
                    <span>{formatPHP((l.rate - (l.discount_type === 'amount' ? l.discount_value : l.rate * l.discount_value / 100)) * l.quantity)}</span>
                  </div>
                ))}
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setDiffOffer(null)} data-testid="diff-sale-cancel">Cancel</Button>
                <Button variant="outline" className="text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                  onClick={openFixFromEdit} data-testid="diff-sale-fix-instead"
                  title="Keep everything on this receipt — the Fix flow collects the difference in the same confirm">
                  Fix &amp; collect here
                </Button>
                <Button className="bg-emerald-700 hover:bg-emerald-800" onClick={openDiffSale} data-testid="diff-sale-open">
                  Open new sale
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={!!balanceWarn} onOpenChange={v => { if (!v) setBalanceWarn(null); }}>
        <DialogContent className="max-w-md" data-testid="edit-balance-warning">
          <DialogHeader>
            <DialogTitle className="text-amber-700">This receipt now has a balance</DialogTitle>
          </DialogHeader>
          {balanceWarn && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">
                The edit changed this receipt's total, so it is no longer fully paid.
                It now shows as a <b>receivable</b>{balanceWarn.customerName ? <> under <b>{balanceWarn.customerName}</b></> : null}.
              </p>
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">New total</span><b>{formatPHP(balanceWarn.total)}</b></div>
                <div className="flex justify-between"><span className="text-slate-500">Paid so far</span><b>{formatPHP(balanceWarn.paid)}</b></div>
                <div className="flex justify-between border-t border-amber-200 pt-1"><span className="text-amber-700 font-semibold">Balance due</span><b className="text-amber-700">{formatPHP(balanceWarn.balance)}</b></div>
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => setBalanceWarn(null)} data-testid="balance-warn-later">
                  Keep as receivable
                </Button>
                {balanceWarn.customerId && (
                  <Button className="bg-emerald-700 hover:bg-emerald-800" data-testid="balance-warn-collect"
                    onClick={() => { setCollectAcctOpen(true); }}>
                    Collect payment now
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {balanceWarn?.customerId && (
        <CustomerAccountModal
          customerId={balanceWarn.customerId}
          customerName={balanceWarn.customerName}
          open={collectAcctOpen}
          branchId={invoice?.branch_id || ''}
          onClose={() => { setCollectAcctOpen(false); setBalanceWarn(null); loadInvoice(); onUpdated?.(); }}
        />
      )}
      <FixReceiptDialog
        open={fixOpen}
        onOpenChange={v => { setFixOpen(v); if (!v) setFixPrefill(null); }}
        invoice={invoice}
        currentBranch={currentBranch}
        prefill={fixPrefill}
        onDone={() => { loadInvoice(); onUpdated?.(); }}
        onVoidReencode={() => { setReopenAfterVoid(true); setVoidOpen(true); }}
        onOpenDiffSale={() => {
          // Fix Phase 4: the copy carries fix_of_invoice_number so the new
          // receipt cross-stamps diff_of_invoice_number and the original
          // backlinks additional_receipts (machine cross-reference law).
          toast.message('Process goods coming back via Returns; the copy prefills the new receipt.');
          sessionStorage.setItem('agb_copy_prefill', JSON.stringify({
            ...invoice, fix_of_invoice_number: invoice?.invoice_number || '',
          }));
          onOpenChange?.(false);
          navigate('/sales-new?copy=1');
        }}
      />
      {/* Compact-edit refund PIN — standard dialog entry (ruling 8) */}
      <Dialog open={!!refundPinAsk} onOpenChange={v => { if (!v) { setRefundPinAsk(null); setRefundPinInput(''); } }}>
        <DialogContent className="max-w-sm" data-testid="edit-refund-pin-dialog">
          <DialogHeader>
            <DialogTitle className="text-amber-700 flex items-center gap-2">
              <ShieldCheck size={16} /> Refund needs authorization
            </DialogTitle>
          </DialogHeader>
          {refundPinAsk && (
            <div className="space-y-3">
              <p className="text-sm text-slate-600">{refundPinAsk.message}</p>
              <Input type="password" autoFocus placeholder="PIN or 6-digit authenticator code"
                value={refundPinInput}
                onChange={e => setRefundPinInput(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && refundPinInput.trim()) {
                    const pin = refundPinInput.trim();
                    setRefundPinAsk(null); setRefundPinInput('');
                    saveCompactEdit(pin);
                  }
                }}
                data-testid="edit-refund-pin-input" />
              <div className="flex gap-1.5">
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-50 border border-emerald-200 text-emerald-700 font-medium">Admin PIN</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-blue-700 font-medium">Manager PIN</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-purple-700 font-medium">TOTP</span>
              </div>
              <div className="flex gap-2 justify-end pt-1">
                <Button variant="outline" onClick={() => { setRefundPinAsk(null); setRefundPinInput(''); }}
                  data-testid="edit-refund-pin-cancel">Cancel</Button>
                <Button className="bg-amber-600 hover:bg-amber-700" disabled={!refundPinInput.trim()}
                  onClick={() => {
                    const pin = refundPinInput.trim();
                    setRefundPinAsk(null); setRefundPinInput('');
                    saveCompactEdit(pin);
                  }}
                  data-testid="edit-refund-pin-confirm">
                  Record refund
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  // Permission gates (Phase B brief item 16, owner-ruled 2026-08-31):
  // `pos.sell` does not exist in the permission model, so these doors were
  // silently admin-only while the backend honored sales.edit / sales.void
  // all along. The gates now match the backend exactly.
  const canEdit = isInvoice && !isVoided && !isExpense && (isAdmin || hasPerm('sales', 'edit'));
  const canVoid = !isVoided && !isExpense && (isAdmin || hasPerm('sales', 'void'));
  const canVerify = !isVerified && !isVoided && (isAdmin || hasPerm('reports', 'view'));
  const canDeleteExpense = isExpense && (isAdmin || hasPerm('accounting', 'view'));
  // Freight editing is blocked on fully-paid walk-in (no customer_id) invoices.
  // No customer = no AR to collect the new balance from. Void + re-issue instead.
  const canEditFreight = canEdit && !(
    invoice?.status === 'paid' && !parseFloat(invoice?.balance || 0) &&
    !invoice?.customer_id
  );

  // ── Edit logic ─────────────────────────────────────────────────────────
  const handleItemChange = (index, field, value) => {
    const newItems = [...editData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    const item = newItems[index];
    const qty = parseFloat(item.quantity) || 0;
    const rate = parseFloat(item.rate) || 0;
    const discVal = parseFloat(item.discount_value) || 0;
    const discType = item.discount_type || 'amount';
    // discount_value is the PER-UNIT amount; multiply by qty to get the line total discount.
    // Percent discounts are already computed as a fraction of the full line value.
    const discAmt = discType === 'percent' ? qty * rate * discVal / 100 : discVal * qty;
    newItems[index].discount_amount = Math.round(discAmt * 100) / 100;
    newItems[index].total = Math.round((qty * rate - discAmt) * 100) / 100;
    setEditData({ ...editData, items: newItems });
  };

  const removeItem = (index) => {
    setEditData({ ...editData, items: editData.items.filter((_, i) => i !== index) });
  };

  const calculateTotals = () => {
    const subtotal = editData.items.reduce((sum, item) => sum + (item.total || 0), 0);
    return { subtotal, grandTotal: subtotal + (editData.freight || 0) - (editData.overall_discount || 0) };
  };

  const handleSave = async () => {
    if (!editReason.trim()) { toast.error('Please provide a reason for the edit'); return; }
    setSaving(true);
    try {
      // Edit-vs-Fix demotion (owner 2026-09-01): only non-financial fields
      // ride an Edit save; money changes go through the fix shell.
      const res = await api.put(`/invoices/${invoice.id}/edit`, {
        customer_name: editData.customer_name, notes: editData.notes,
        reason: editReason,
        proof_url: proofFile ? proofFile.name : null,
        _collection: invoice._collection || 'invoices',
      });
      toast.success(res.data.message);
      maybeWarnNewBalance(res.data?.invoice, invoice);
      setEditMode(false); setEditReason(''); setProofFile(null);
      loadInvoice();
      onUpdated?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save changes';
      if (typeof msg === 'string' && msg.startsWith('WALKIN_BALANCE_BLOCKED')) {
        const lines = computeDiffLines();
        if (lines.length > 0) setDiffOffer({ lines, message: msg.replace('WALKIN_BALANCE_BLOCKED: ', '') });
        else openFixFromEdit();  // guided path (Phase 3): Fix collects atomically
      } else {
        toast.error(msg);
      }
    }
    setSaving(false);
  };

  // ── Quick freight save ────────────────────────────────────────────────
  const handleFreightSave = async () => {
    const val = parseFloat(freightInput);
    if (isNaN(val) || val < 0) { setFreightError('Enter a valid freight amount (0 or more)'); return; }
    setFreightSaving(true); setFreightError('');
    try {
      const body = {
        freight: val,
        reason: freightReason.trim() || `Freight ${val > 0 ? 'added' : 'cleared'}`,
        _collection: invoice._collection || 'invoices',
      };
      if (freightPin.trim()) body.pin = freightPin.trim();
      const res = await api.put(`/invoices/${invoice.id}/edit`, body);
      toast.success(`Freight updated — ${res.data.message || 'saved'}`);
      maybeWarnNewBalance(res.data?.invoice, invoice);
      setFreightEditOpen(false);
      setFreightInput(''); setFreightReason(''); setFreightPin(''); setFreightError('');
      loadInvoice(); onUpdated?.();
      // Reload freight history
      setFreightHistoryLoaded(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Failed to save freight');
      // Closed-day requires PIN — surface clearly
      if (typeof msg === 'string' && /closed.*day|manager.*pin|late.*encode/i.test(msg)) {
        setFreightError('Day is closed — enter a manager PIN and reason below');
      } else {
        setFreightError(msg);
      }
    }
    setFreightSaving(false);
  };

  // ── Actions ────────────────────────────────────────────────────────────
  const handleVerify = async () => {
    if (!verifyPin) { toast.error('Enter PIN'); return; }
    setActionLoading(true);
    try {
      const docType = isPO ? 'purchase_order' : 'invoice';
      await api.post(`/verify/${docType}/${invoice.id}`, { pin: verifyPin });
      toast.success('Transaction verified');
      setVerifyOpen(false); setVerifyPin('');
      loadInvoice(); onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Verification failed'); }
    setActionLoading(false);
  };

  const handleVoid = async () => {
    if (!voidReason || !voidPin) { toast.error('Reason and PIN required'); return; }
    setActionLoading(true);
    try {
      let voidRes = null;
      if (isPO) {
        await api.delete(`/purchase-orders/${invoice.id}`);
        toast.success('PO cancelled');
      } else if (isExpense) {
        await api.delete(`/expenses/${invoice.id}`);
        toast.success('Expense deleted');
      } else {
        voidRes = await api.post(`/invoices/${invoice.id}/void`, { reason: voidReason, manager_pin: voidPin });
        toast.success('Invoice voided');
      }
      setVoidOpen(false); setVoidReason(''); setVoidPin('');

      if (reopenAfterVoid && voidRes?.data?.snapshot) {
        try { sessionStorage.setItem('reopen_sale_snapshot', JSON.stringify(voidRes.data.snapshot)); } catch { /* ignore */ }
        onOpenChange?.(false);
        onUpdated?.();
        navigate('/sales-new');
        setActionLoading(false);
        setReopenAfterVoid(false);
        return;
      }
      setReopenAfterVoid(false);
      loadInvoice(); onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    setActionLoading(false);
  };

  // ── Refund: navigate to /returns with the invoice prefilled ───────────
  const handleRefund = () => {
    if (!invoice) return;
    const invNum = invoice.invoice_number || invoice.sale_number || '';
    onOpenChange?.(false);
    navigate(`/returns?invoice=${encodeURIComponent(invNum)}`);
  };

  const openVoidDialog = (alsoReopen) => {
    setReopenAfterVoid(!!alsoReopen);
    setVoidReason(''); setVoidPin('');
    setVoidOpen(true);
  };

  const copyNumber = async () => {
    try { await navigator.clipboard.writeText(docNumber); } catch { /* fallback */ }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  const loadEditHistory = async () => {
    if (!invoice?.id) return;
    try {
      const [eRes, pRes] = await Promise.all([
        api.get(`/invoices/${invoice.id}/edit-history`),
        api.get(`/invoices/${invoice.id}/print-history`).catch(() => ({ data: null })),
      ]);
      setEditHistory(eRes.data);
      setPrintHistory(pRes.data);
      setSection('history');
    } catch { toast.error('Failed to load edit history'); }
  };

  // Add-on eligibility (owner decision 2026-07-29): items may be added to a
  // live receipt — no BIR invoice issued AND the sale's day still open.
  // Print history is fetched alongside so the compact layout can show the
  // print trail inline without a separate tab click.
  useEffect(() => {
    setAmendable(null);
    setPrintHistory(null);
    if (!invoice?.id || expenseId || !Array.isArray(invoice?.items)) return;
    api.get(`/invoices/${invoice.id}/amendable`)
      .then(r => setAmendable(r.data))
      .catch(() => setAmendable(null));
    api.get(`/invoices/${invoice.id}/print-history`)
      .then(r => setPrintHistory(r.data))
      .catch(() => setPrintHistory(null));
  }, [invoice?.id, expenseId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Print (compact mode) ──────────────────────────────────────────────
  const handlePrint = async (format, extras = null) => {
    if (!invoice) return;
    const docType = PrintEngine.getDocType(invoice);
    let docCode = invoice.doc_code || '';
    if (!docCode && invoice.id) {
      try {
        const res = await api.post('/doc/generate-code', { doc_type: 'invoice', doc_id: invoice.id });
        docCode = res.data?.code || '';
        setInvoice(prev => prev ? { ...prev, doc_code: docCode } : prev);
      } catch { /* print without QR */ }
    }
    // Pick the most recent signed signature (or bypass) to embed in the receipt
    const signedSig = signatures.find(s => s.status === 'signed' && s.signature_url);
    const bypassedSig = signatures.find(s => s.status === 'bypassed');
    const activeSig = signedSig || bypassedSig;
    const printData = {
      ...invoice,
      signature_url: signedSig?.signature_url || null,
      bypass_method: !signedSig && bypassedSig ? (bypassedSig.bypass_method || 'pin') : null,
      signature_signed_at: activeSig?.signed_at || activeSig?.bypassed_at || null,
      signature_verification_token: activeSig?.verification_token || null,
    };
    PrintEngine.print({ type: docType, data: printData, format, businessInfo, docCode, extras });
  };

  // Packing slip — price-free trucker copy. No doc code on purpose: the QR's
  // /doc page shows prices, which is exactly what this document must hide.
  const handlePrintPackingSlip = (format) => {
    if (!invoice) return;
    PrintEngine.print({ type: 'packing_slip', data: invoice, format, businessInfo, docCode: '' });
  };

  // ── Generate HTML for Remote Print ────────────────────────────────────
  const getRemotePrintHtml = (format = 'full_page') => {
    if (!invoice) return '';
    const docType = PrintEngine.getDocType(invoice);
    const docCode = invoice.doc_code || '';
    return PrintEngine.generateHtml({ type: docType, data: invoice, format, businessInfo, docCode });
  };

  const getRemotePrintDocType = () => {
    if (!invoice) return 'sales_receipt';
    if (isPO) return 'purchase_order';
    if (isExpense) return 'expense_receipt';
    return 'sales_receipt';
  };

  const getReceiverDocType = () => {
    if (!invoice) return 'invoice';
    if (isPO) return 'purchase_order';
    if (isExpense) return 'expense';
    return 'invoice';
  };

  // ── Compact edit helpers ──────────────────────────────────────────────
  const openCompactEdit = () => {
    setEditData({
      items: JSON.parse(JSON.stringify(invoice.items || [])),
      customer_name: invoice.customer_name || '',
      notes: invoice.notes || '',
      freight: invoice.freight || 0,
      overall_discount: invoice.overall_discount || 0,
    });
    setEditReason('');
    setEditOrderDate(invoice.order_date || fmtDate(invoice.created_at) || '');
    const branchId = invoice.branch_id || currentBranch?.id;
    if (branchId && invoice.order_date) {
      api.get('/invoices/check-date-closed', { params: { date: invoice.order_date, branch_id: branchId } })
        .then(r => setEditDateClosed(r.data.closed))
        .catch(() => setEditDateClosed(false));
    }
    setEditMode(true);
  };

  const saveCompactEdit = async (refundPin = '') => {
    if (!editReason.trim()) { toast.error('Please provide a reason for the edit'); return; }
    setSaving(true);
    try {
      // Edit-vs-Fix demotion (owner 2026-09-01): items/freight/discount no
      // longer ride an Edit save - money changes go through the fix shell.
      const payload = {
        notes: editData.notes, reason: editReason,
        customer_name: editData.customer_name,
        branch_id: invoice.branch_id || currentBranch?.id,
      };
      if (refundPin) payload.pin = refundPin;
      if (editOrderDate && editOrderDate !== (invoice.order_date || fmtDate(invoice.created_at))) {
        payload.order_date = editOrderDate;
        payload.invoice_date = editOrderDate;
      }
      const res = await api.put(`/invoices/${invoice.id}/edit`, payload);
      if (res.data?.refund?.amount) {
        toast.success(
          `Sale updated — refund ₱${Number(res.data.refund.amount).toLocaleString('en-PH', { minimumFractionDigits: 2 })} recorded. Hand the cash back to the customer.`,
          { duration: 12000 },
        );
      } else {
        toast.success('Sale updated');
      }
      maybeWarnNewBalance(res.data?.invoice, invoice);
      setEditMode(false);
      loadInvoice();
      onUpdated?.();
    } catch (e) {
      const msg = e.response?.data?.detail || 'Failed to save';
      // Edit dropped the total below the amount already paid → a real refund
      // must be recorded, and that needs a manager PIN (F-16, 2026-07-29).
      if (typeof msg === 'string' && msg.startsWith('WALKIN_BALANCE_BLOCKED')) {
        const lines = computeDiffLines();
        if (lines.length > 0) setDiffOffer({ lines, message: msg.replace('WALKIN_BALANCE_BLOCKED: ', '') });
        else openFixFromEdit();  // guided path (Phase 3): Fix collects atomically
      } else if (typeof msg === 'string' && msg.startsWith('REFUND_PIN_REQUIRED')) {
        // Standard PIN dialog (ruling 8) — the raw browser prompt() is retired.
        setRefundPinAsk({ message: msg.replace('REFUND_PIN_REQUIRED: ', '') });
      } else {
        toast.error(msg);
      }
    }
    setSaving(false);
  };

  // ── Compact void ──────────────────────────────────────────────────────
  const handleCompactVoid = async () => {
    if (!voidReason || !voidPin) { toast.error('Reason and PIN required'); return; }
    setActionLoading(true);
    try {
      const res = await api.post(`/invoices/${invoice.id}/void`, { reason: voidReason, manager_pin: voidPin });
      toast.success('Sale voided');
      setVoidOpen(false); setVoidReason(''); setVoidPin('');

      if (reopenAfterVoid && res?.data?.snapshot) {
        try { sessionStorage.setItem('reopen_sale_snapshot', JSON.stringify(res.data.snapshot)); } catch { /* ignore */ }
        onOpenChange?.(false);
        onUpdated?.();
        navigate('/sales-new');
        setActionLoading(false);
        setReopenAfterVoid(false);
        return;
      }
      setReopenAfterVoid(false);
      loadInvoice(); onUpdated?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to void'); }
    setActionLoading(false);
  };

  // ── Type info ──────────────────────────────────────────────────────────
  const getTypeInfo = () => {
    if (!invoice) return { label: 'Invoice', color: 'bg-blue-100 text-blue-700' };
    if (isPO) return { label: 'Purchase Order', color: 'bg-amber-100 text-amber-700' };
    if (isExpense) return { label: invoice.category || 'Expense', color: 'bg-red-100 text-red-700' };
    const st = invoice.sale_type || '';
    if (st === 'interest_charge') return { label: 'Interest Charge', color: 'bg-amber-100 text-amber-700' };
    if (st === 'penalty_charge') return { label: 'Penalty Charge', color: 'bg-red-100 text-red-700' };
    if (st === 'farm_expense') return { label: 'Farm Expense', color: 'bg-green-100 text-green-700' };
    if (st === 'cash_advance') return { label: 'Customer Cash Out', color: 'bg-purple-100 text-purple-700' };
    return { label: 'Sales Invoice', color: 'bg-blue-100 text-blue-700' };
  };

  const statusStyles = {
    paid: 'bg-emerald-100 text-emerald-700', partial: 'bg-amber-100 text-amber-700',
    open: 'bg-red-100 text-red-700', received: 'bg-emerald-100 text-emerald-700',
    draft: 'bg-slate-100 text-slate-600', voided: 'bg-slate-200 text-slate-500',
    cancelled: 'bg-red-100 text-red-600',
  };

  const typeInfo = getTypeInfo();
  const { subtotal, grandTotal } = calculateTotals();

  // ══════════════════════════════════════════════════════════════════════
  // COMPACT MODE — single-view layout (replaces SaleDetailModal / A4)
  // ══════════════════════════════════════════════════════════════════════
  if (compact) {
    const saleNumber = invoice?.invoice_number || invoice?.sale_number || '';

    return (
      <>
        <Dialog open={open} modal={!(uploadQROpen || viewQROpen || verifyDialogOpen || receiptQROpen || payHistoryOpen || !!ppOpenNumber)} onOpenChange={v => { onOpenChange(v); if (!v) setEditMode(false); }}>
          <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-testid="invoice-detail-modal"
            onInteractOutside={(e) => hopBackInsteadOfClose(e, uploadQROpen || viewQROpen || verifyDialogOpen || receiptQROpen || payHistoryOpen || ppOpenNumber)}
            onEscapeKeyDown={(e) => hopBackInsteadOfClose(e, uploadQROpen || viewQROpen || verifyDialogOpen || receiptQROpen || payHistoryOpen || ppOpenNumber)}>
            <DialogHeader>
              {hopStack.length > 0 && (
                <button type="button" onClick={hopBack}
                  className="self-start inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                  data-testid="sale-hop-back">
                  <ArrowLeft size={12} /> Back to {hopStack[hopStack.length - 1].pp || hopStack[hopStack.length - 1].number || 'previous receipt'}
                </button>
              )}
              <DialogTitle style={{ fontFamily: 'Manrope' }} data-testid="invoice-number">
                {editMode ? `Edit Details — ${saleNumber}` : `Sale Detail — ${saleNumber}`}
              </DialogTitle>
              <div className="mt-1 flex items-center gap-2 flex-wrap">
                {invoice?.verified && (
                  <>
                    <VerificationBadge doc={invoice} />
                    {invoice.verified_at && <span className="text-[10px] text-slate-400">{fmtDateTime(invoice.verified_at)}</span>}
                  </>
                )}
                {/* Verify is a CHECK on the sale, not a way to print it — it sits
                    with the state it changes (owner layout 2026-09-21). */}
                {invoice && !invoice.verified && !isVoided && !editMode && (
                  <>
                    <span className="inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200"
                      data-testid="sale-not-verified-chip">Not verified</span>
                    <Button size="sm" variant="outline" className="h-6 px-2 text-[11px] text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                      onClick={() => setVerifyDialogOpen(true)} data-testid="sale-verify-btn">
                      <ShieldCheck size={11} className="mr-1" /> Verify
                    </Button>
                  </>
                )}
                {(invoice?.has_signature || signatures.length > 0) && (
                  <button
                    type="button"
                    onClick={() => {
                      setSection('signature');
                      // In compact mode the section is inline — scroll into view
                      setTimeout(() => {
                        const el = document.getElementById('compact-signature-section');
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                      }, 80);
                    }}
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded border transition-colors ${
                      (invoice?.signature_status === 'bypassed' || signatures[0]?.status === 'bypassed')
                        ? 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                        : 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                    }`}
                    title={(invoice?.signature_status === 'bypassed' || signatures[0]?.status === 'bypassed') ? 'Authorized via Manager PIN — click to view' : 'Customer signed — click to view'}
                    data-testid="header-signature-chip"
                  >
                    <ShieldCheck size={11} />
                    {(invoice?.signature_status === 'bypassed' || signatures[0]?.status === 'bypassed') ? 'PIN bypass' : 'Signed by customer'}
                  </button>
                )}
              </div>
            </DialogHeader>

            {loading ? (
              <div className="flex items-center justify-center py-12"><div className="text-slate-400">Loading...</div></div>
            ) : invoice ? (
              <>
                {/* Actions, grouped by the job they do (owner layout 2026-09-21 — the
                    bar had grown to 13 same-size buttons in the order they were added):
                    give a copy · change this sale. Verify moved up beside the status it
                    changes; Upload / View on phone moved into the Receipt photos panel. */}
                <div className="space-y-2.5 pb-3 border-b border-slate-100" data-testid="sale-action-bar">
                  <div data-testid="sale-actions-copy">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Give a copy</p>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <div className="inline-flex">
                        <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap rounded-r-none"
                          onClick={() => printAs(lastPrintFormat)} data-testid="sale-print-btn"
                          title={`Print with the format this device used last (${PRINT_FORMATS[lastPrintFormat]})`}>
                          <Printer size={13} className="mr-1.5" /> Print · {PRINT_FORMATS[lastPrintFormat]}
                        </Button>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 px-1.5 rounded-l-none border-l-0"
                              aria-label="More print options" data-testid="sale-print-more">
                              <ChevronDown size={13} />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-56">
                            <DropdownMenuLabel className="text-[11px] font-normal text-slate-500">Print this receipt as…</DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => printAs('thermal')} data-testid="sale-print-thermal">
                              <Printer size={14} className="mr-2 text-slate-500" /> Thermal (58mm)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => printAs('dot_matrix')} data-testid="sale-print-dot-matrix">
                              <Printer size={14} className="mr-2 text-slate-500" /> 8.5×11 Dot Matrix
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => setSendToPrintOpen(true)} className="text-emerald-700" data-testid="sale-send-to-print-btn">
                              <Send size={14} className="mr-2" /> Send to Branch Printer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                      {isInvoice && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-blue-800 border-blue-300 hover:bg-blue-50"
                              title="Price-free copy for truckers / delivery" data-testid="sale-packing-slip-btn">
                              <Truck size={13} className="mr-1.5" /> Packing Slip <ChevronDown size={12} className="ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start" className="w-60">
                            <DropdownMenuLabel className="text-[11px] font-normal text-slate-500">
                              Items &amp; quantities only — <b className="text-slate-700">no prices</b>
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handlePrintPackingSlip('thermal')} data-testid="packing-slip-thermal">
                              <Printer size={14} className="mr-2 text-slate-500" /> Thermal (58mm)
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePrintPackingSlip('dot_matrix')} data-testid="packing-slip-dot-matrix">
                              <Printer size={14} className="mr-2 text-slate-500" /> 8.5×11 Dot Matrix
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => setPackingSendOpen(true)} className="text-emerald-700" data-testid="packing-slip-remote">
                              <Send size={14} className="mr-2" /> Send to Branch Printer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      {isInvoice && (
                        <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                          onClick={() => setReceiptQROpen(true)} data-testid="sale-receipt-qr-btn"
                          title="Show the customer's digital receipt as a QR to scan, or copy its link to send">
                          <QrCode size={13} className="mr-1.5" /> Receipt QR
                        </Button>
                      )}
                    </div>
                  </div>

                  {!editMode && (canEdit || isInvoice) && (
                    <div data-testid="sale-actions-change">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                        {canEdit ? 'Change this sale' : 'Sell it again'}
                      </p>
                      <div className="flex flex-wrap items-center gap-1.5">
                        {canEdit && amendable?.allowed && (
                          <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                            onClick={() => { onOpenChange?.(false); navigate(`/sales-new?amend=${invoice.id}`); }}
                            title="Add items to this receipt (no BIR invoice yet, day still open)"
                            data-testid="amend-add-items-btn">
                            <Package size={13} className="mr-1.5" /> Add Items
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                            onClick={() => {
                              // Door consolidation (owner 2026-08-31): the ONE
                              // correction door is the /sales-new shell. The old
                              // FixReceiptDialog stays in-tree, unreferenced.
                              onOpenChange?.(false);
                              navigate(`/sales-new?fix=${invoice.id}`);
                            }}
                            title="Correct this receipt in the full sales grid"
                            data-testid="fix-receipt-btn">
                            <RefreshCw size={13} className="mr-1.5" /> Correct This Receipt
                          </Button>
                        )}
                        {canEdit && (
                          <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap text-amber-600 border-amber-200 hover:bg-amber-50"
                            onClick={openCompactEdit} data-testid="sale-edit-btn">
                            <Pencil size={13} className="mr-1.5" /> Edit
                          </Button>
                        )}
                        {isInvoice && (
                          <Button size="sm" variant="outline" className="h-8 text-xs whitespace-nowrap border-dashed text-slate-700"
                            onClick={copyReceipt} data-testid="sale-copy-receipt-btn"
                            title="Start a NEW sale with the same items, prices and discounts — you review it before saving. This receipt is not changed.">
                            <Copy size={13} className="mr-1.5" /> Repeat Order
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="space-y-4">
                  {/* Returns & refunds on this receipt (audit Group B C4b, owner decision 8) */}
                  {(invoice.returns_summary || []).filter(r => !r.voided).length > 0 && (
                    <div className="rounded-lg border border-orange-200 bg-orange-50/50 px-4 py-3" data-testid="invoice-returns-section">
                      <p className="text-xs font-bold text-orange-800 mb-1.5">Returns on this receipt</p>
                      {(invoice.returns_summary || []).filter(r => !r.voided).map((r, i) => (
                        <div key={`ret-${i}`} className="text-xs text-slate-700 border-t border-orange-100 py-1.5 first:border-t-0">
                          <div className="flex justify-between flex-wrap gap-1">
                            <span className="font-semibold">{r.rma_number}</span>
                            <span className="text-slate-500">{r.return_date}</span>
                          </div>
                          <div className="text-slate-600">
                            {(r.items || []).map(it => `${it.product_name} x ${it.quantity}${it.inventory_action === 'pullout' ? ' (pulled out)' : ''}`).join(', ')}
                          </div>
                          <div className="flex gap-3 flex-wrap mt-0.5">
                            {r.cash_refunded > 0 && <span>Cash refunded: <b>{formatPHP(r.cash_refunded)}</b></span>}
                            {r.digital_refunded > 0 && <span>Digital refunded: <b>{formatPHP(r.digital_refunded)}</b></span>}
                            {r.ar_credited > 0 && <span>Applied to balance: <b>{formatPHP(r.ar_credited)}</b></span>}
                            {r.pullout_loss > 0 && <span className="text-red-600">Stock loss: <b>{formatPHP(r.pullout_loss)}</b></span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Receipt photos — Upload / View on phone live WITH the photos */}
                  <ReceiptGallery recordType="invoice" recordId={invoice.id}
                    title="Receipt photos"
                    refreshKey={photosVersion}
                    renderActions={({ totalFiles }) => (
                      <>
                        <Button size="sm" variant="outline" className="h-7 text-xs whitespace-nowrap"
                          onClick={() => setUploadQROpen(true)} data-testid="sale-upload-receipt-btn">
                          <Upload size={12} className="mr-1" /> Upload
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-xs whitespace-nowrap"
                          disabled={totalFiles === 0}
                          title={totalFiles === 0 ? 'No photos uploaded yet' : 'Open these photos on a phone (link lasts 1 hour)'}
                          onClick={() => setViewQROpen(true)} data-testid="sale-view-phone-btn">
                          <Smartphone size={12} className="mr-1" /> View on Phone
                        </Button>
                      </>
                    )} />

                  {/* Header info */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-slate-500">Customer:</span> <b>{invoice.customer_name || 'Walk-in'}</b></div>
                    <div><span className="text-slate-500">Date:</span> {invoice.order_date || fmtDate(invoice.created_at)}</div>
                    <div><span className="text-slate-500">Cashier:</span> {invoice.cashier_name || '—'}</div>
                    <div><span className="text-slate-500">Payment:</span> <Badge variant="outline" className="text-[10px]">{invoice.payment_method || 'Cash'}</Badge></div>
                    <div><span className="text-slate-500">Status:</span>
                      <Badge className={`text-[10px] ml-1 ${isVoided ? 'bg-red-100 text-red-700' : invoice.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : invoice.status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'}`}>
                        {invoice.status || 'completed'}
                      </Badge>
                      {(invoice.returns_summary || []).some(r => !r.voided) && (
                        <Badge className="text-[10px] ml-1 bg-orange-100 text-orange-700" data-testid="invoice-returned-badge">
                          Returned / Refunded
                        </Badge>
                      )}
                    </div>
                    {invoice.terms && <div><span className="text-slate-500">Terms:</span> {invoice.terms_label || invoice.terms}</div>}
                    {invoice.due_date && <div><span className="text-slate-500">Due:</span> {invoice.due_date}</div>}
                    {invoice.customer_po && <div data-testid="invoice-customer-po"><span className="text-slate-500">Ref PO:</span> {invoice.customer_po}</div>}
                    {invoice.sales_rep_name && <div><span className="text-slate-500">Sales Rep:</span> {invoice.sales_rep_name}</div>}
                    {invoice.balance > 0 && <div><span className="text-slate-500">Balance:</span> <b className="text-red-600">{formatPHP(invoice.balance)}</b></div>}
                  </div>

                  {/* Digital payment info */}
                  {(invoice.digital_platform || invoice.fund_source === 'digital' || invoice.fund_source === 'split') && (
                    <div className="grid grid-cols-2 gap-3 text-sm bg-slate-50 rounded-lg p-3">
                      {invoice.digital_platform && <div><span className="text-slate-500">Platform:</span> <span className="flex items-center gap-1 inline-flex"><Wallet size={12} className="text-blue-500" />{invoice.digital_platform}</span></div>}
                      {invoice.digital_ref_number && <div><span className="text-slate-500">Ref #:</span> <span>{invoice.digital_ref_number}</span></div>}
                      {invoice.digital_sender && <div><span className="text-slate-500">Sender:</span> {invoice.digital_sender}</div>}
                      {invoice.fund_source && <div><span className="text-slate-500">Fund Source:</span> <span className="capitalize">{invoice.fund_source}</span></div>}
                      {invoice.cash_amount > 0 && <div><span className="text-slate-500">Cash Portion:</span> {formatPHP(invoice.cash_amount)}</div>}
                      {invoice.digital_amount > 0 && <div><span className="text-slate-500">Digital Portion:</span> {formatPHP(invoice.digital_amount)}</div>}
                    </div>
                  )}

                  {/* Items table / edit mode */}
                  {editMode ? (
                    <div className="space-y-2">
                      {/* Edit-vs-Fix demotion (owner 2026-09-01): money fields (items,
                          freight, discount) belong to the fix shell - preview + tier
                          PIN. Edit keeps only non-financial details. */}
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-2 flex-wrap"
                        data-testid="edit-demoted-note">
                        <span>Items, freight &amp; discount are money fields — correct them with the full preview and PIN.</span>
                        <Button size="sm" variant="outline" className="h-6 text-[11px] border-emerald-300"
                          onClick={() => { onOpenChange?.(false); navigate(`/sales-new?fix=${invoice.id}`); }}>
                          Correct This Receipt
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500 font-medium uppercase">Items (read-only)</p>
                      {editData.items.map((item, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1.5 items-center p-2 bg-slate-50 rounded-lg border border-slate-200">
                          <div className="col-span-5 text-xs font-medium truncate">{item.product_name}</div>
                          <div className="col-span-3 text-right text-sm">{item.quantity}</div>
                          <div className="col-span-4 text-right text-sm">{formatPHP(item.rate || item.unit_price || item.price || 0)}</div>
                        </div>
                      ))}
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-xs text-slate-500">Sale Date</Label>
                          <Input type="date" value={editOrderDate}
                            onChange={e => setEditOrderDate(e.target.value)}
                            className="mt-1 h-9 text-sm" />
                          {editDateClosed && editOrderDate === (invoice.order_date || fmtDate(invoice.created_at)) && (
                            <p className="text-[9px] text-amber-600 mt-0.5">This date is closed. PIN required to save changes.</p>
                          )}
                        </div>
                        <div>
                          <Label className="text-xs text-slate-500">Reason for Edit <span className="text-red-500">*</span></Label>
                          <Input value={editReason} onChange={e => setEditReason(e.target.value)}
                            placeholder="e.g. Customer correction, wrong item..."
                            className="mt-1 h-9 text-sm" />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t">
                        <Button variant="outline" onClick={() => setEditMode(false)} className="flex-1">Cancel</Button>
                        <Button onClick={() => saveCompactEdit()} disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white">
                          {saving ? <RefreshCw size={13} className="animate-spin mr-1.5" /> : <Check size={13} className="mr-1.5" />}
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                    <div className="flex justify-end mb-1">{capitalToggleBtn}</div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="text-xs">Product</TableHead>
                          <TableHead className="text-xs text-right">Qty</TableHead>
                          <TableHead className="text-xs text-right">Price</TableHead>
                          <TableHead className="text-xs text-right">Disc/Unit</TableHead>
                          <TableHead className="text-xs text-right">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {invoice.items?.map((item, i) => (
                          <TableRow key={i}>
                            <TableCell className="text-sm">
                              {item.product_name}
                              {item.is_repack && <Badge variant="outline" className="text-[9px] ml-1">R</Badge>}
                              {lineDescriptionFor(item)}
                              {capitalRefsFor(item)}
                            </TableCell>
                            <TableCell className="text-right">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatPHP(item.rate || item.unit_price || item.price || 0)}</TableCell>
                            <TableCell className="text-right text-xs text-emerald-600">
                              {(() => {
                                const dv = parseFloat(item.discount_value) || 0;
                                const da = parseFloat(item.discount_amount) || 0;
                                const dt = item.discount_type || 'amount';
                                if (dv > 0) return dt === 'percent' ? `-${dv}%` : `-${formatPHP(dv)}/unit`;
                                if (da > 0) {
                                  const qty = parseFloat(item.quantity) || 1;
                                  return `-${formatPHP(Math.round(da / qty * 100) / 100)}/unit`;
                                }
                                return '—';
                              })()}
                            </TableCell>
                            <TableCell className="text-right font-semibold">{formatPHP(item.total || 0)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    </>
                  )}

                  {!editMode && (
                    <>
                      <div className="text-sm space-y-1 border-t pt-3">
                        <div className="flex justify-between"><span className="text-slate-500">Subtotal</span><span>{formatPHP(invoice.subtotal || invoice.line_subtotal || 0)}</span></div>
                        {(invoice.overall_discount || invoice.overall_discount_amount || 0) > 0 && (
                          <div className="flex justify-between text-emerald-600"><span>Discount</span><span>-{formatPHP(invoice.overall_discount || invoice.overall_discount_amount || 0)}</span></div>
                        )}
                        {(invoice.freight || 0) > 0
                          ? <div className="flex justify-between items-center">
                              <span className="text-slate-500">Freight</span>
                              <div className="flex items-center gap-2">
                                <span>{formatPHP(invoice.freight)}</span>
                                {canEditFreight && !editMode && (
                                  <button onClick={() => { setFreightInput(String(invoice.freight || '')); setFreightEditOpen(true); }}
                                    className="text-[10px] text-slate-400 hover:text-blue-600 underline" data-testid="edit-freight-btn">Edit</button>
                                )}
                              </div>
                            </div>
                          : canEditFreight && !editMode && (
                            <div className="flex justify-between items-center">
                              <span className="text-slate-400">Freight</span>
                              <button onClick={() => { setFreightInput(''); setFreightEditOpen(true); }}
                                className="text-[10px] text-blue-500 hover:text-blue-700 underline" data-testid="add-freight-btn">+ Add Freight</button>
                            </div>
                          )}
                        {(invoice.tax_amount || 0) > 0 && <div className="flex justify-between"><span className="text-slate-500">Tax</span><span>{formatPHP(invoice.tax_amount)}</span></div>}
                        <div className="flex justify-between font-bold text-base pt-1 border-t"><span>Grand Total</span><span className="text-[#1A4D2E]">{formatPHP(invoice.grand_total || invoice.total || 0)}</span></div>
                        {(invoice.amount_paid || 0) > 0 && <div className="flex justify-between text-sm text-emerald-600"><span>Paid</span><span>{formatPHP(invoice.amount_paid)}</span></div>}
                        {(invoice.balance || 0) > 0 && <div className="flex justify-between text-sm text-red-600"><span>Balance</span><span>{formatPHP(invoice.balance)}</span></div>}
                      </div>

                      {/* Freight change history — shown when freight was edited at least once */}
                      {freightHistory.length > 0 && (
                        <div className="bg-blue-50 border border-blue-100 rounded-lg px-3 py-2 space-y-1.5" data-testid="freight-history">
                          <p className="text-[10px] font-semibold text-blue-700 uppercase tracking-wide">Freight Change History</p>
                          {freightHistory.map((h, i) => (
                            <div key={i} className="flex items-start justify-between text-[11px]">
                              <div className="flex-1 min-w-0">
                                <span className={`font-semibold ${h.freight_delta > 0 ? 'text-blue-700' : h.freight_delta < 0 ? 'text-red-600' : 'text-slate-500'}`}>
                                  {h.freight_delta > 0 ? '+' : ''}{formatPHP(h.freight_delta)}
                                </span>
                                <span className="text-slate-500 ml-1">
                                  ({formatPHP(h.freight_from)} → {formatPHP(h.freight_to)})
                                </span>
                                {h.reason && <span className="text-slate-400 ml-1 italic">· "{h.reason}"</span>}
                                <p className="text-[10px] text-slate-400">{h.edited_by_name} · {h.edited_at ? new Date(h.edited_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {invoice.notes && <p className="text-sm text-slate-500 border-t pt-2">Notes: {invoice.notes}</p>}

                      {/* Sales rep / created info */}
                      <div className="text-xs text-slate-400 space-y-0.5 border-t pt-2">
                        {invoice.sales_rep_name && <p>Sales Rep: {invoice.sales_rep_name}</p>}
                        <p>Created: {invoice.created_at ? formatDateTime(invoice.created_at) : '—'} by {invoice.cashier_name || '—'}</p>
                        {invoice.edited && <p>Last edited: {invoice.last_edited_at ? formatDateTime(invoice.last_edited_at) : '—'} by {invoice.last_edited_by || '—'}</p>}
                      </div>

                      {/* Payment history: one line + a link; the list lives in its own
                          dialog with each Proof of Payment clickable (owner 2026-09-22). */}
                      {isInvoice && (
                        <div className="border-t pt-2 flex items-center justify-between gap-2 flex-wrap" data-testid="sale-payment-summary">
                          <p className="text-xs text-slate-600">
                            <span className="font-semibold uppercase text-slate-400 mr-2">Payments</span>
                            {paymentSummaryLine(invoice)}
                          </p>
                          {payments.length > 0 && (
                            <button type="button" onClick={() => setPayHistoryOpen(true)}
                              className="text-xs text-blue-700 hover:underline whitespace-nowrap"
                              data-testid="sale-view-payment-history">
                              View payment history
                            </button>
                          )}
                        </div>
                      )}

                      {/* Versions + print trail (owner feature 2026-07-29) —
                          full snapshot per saved edit with per-line diffs,
                          plus every recorded print of this document. */}
                      {(invoice.edit_history?.length > 0 || printHistory?.events?.length > 0) && (
                        <div className="border-t pt-3">
                          <VersionsAndPrints invoice={invoice} editsDesc={invoice.edit_history || []} printHistory={printHistory} />
                        </div>
                      )}

                      {/* Customer signature(s) — inline in compact mode */}
                      {signatures.length > 0 && (
                        <div id="compact-signature-section" data-testid="signature-section" className="border-2 border-emerald-200 rounded-lg p-3 bg-emerald-50/30 space-y-2">
                          <h4 className="text-xs font-semibold flex items-center gap-1.5 text-emerald-700">
                            <ShieldCheck size={13} /> Credit Authorization Signature
                          </h4>
                          {signatures.map((sig, i) => (
                            <div key={sig.id || i} className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <div className="flex items-center justify-between mb-2">
                                <Badge className={`text-[10px] ${sig.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {sig.status === 'signed' ? '✓ Signed' : 'PIN Override'}
                                </Badge>
                                <span className="text-[10px] text-slate-400">
                                  {sig.signed_at ? formatDateTime(sig.signed_at) : sig.bypassed_at ? formatDateTime(sig.bypassed_at) : ''}
                                </span>
                              </div>
                              {sig.status === 'signed' && sig.signature_url ? (
                                <div className="border border-dashed border-slate-300 rounded p-2 bg-white">
                                  <img src={sig.signature_url} alt="Customer signature" className="max-h-24 mx-auto object-contain"
                                    onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }} />
                                  <p className="text-[10px] text-slate-400 hidden text-center py-2">Image unavailable.</p>
                                  <div className="flex justify-center mt-1.5">
                                    <p className="text-[9px] text-slate-400 uppercase tracking-wider">Customer signature · {sig.signer_name || sig.credit_context?.customer_name || ''}</p>
                                  </div>
                                </div>
                              ) : sig.status === 'bypassed' ? (
                                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-[11px] text-amber-700">
                                  <p className="font-medium">Manager PIN Override · By {sig.bypass_by_name}</p>
                                  {sig.bypass_reason && <p className="italic">"{sig.bypass_reason}"</p>}
                                </div>
                              ) : (
                                <p className="text-[11px] text-slate-400 text-center py-2">Status: {sig.status}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Action buttons: Refund / Void / Void & Re-open */}
                      {canVoid && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          <Button variant="outline" className="w-full text-amber-700 border-amber-200 hover:bg-amber-50" onClick={handleRefund} data-testid="sale-refund-btn">
                            <RotateCcw size={14} className="mr-2" /> Refund
                          </Button>
                          <Button variant="destructive" className="w-full" onClick={() => openVoidDialog(false)} data-testid="sale-void-btn">
                            <Ban size={14} className="mr-2" /> Void
                          </Button>
                          <Button variant="outline" className="w-full text-red-700 border-red-200 hover:bg-red-50" onClick={() => openVoidDialog(true)} data-testid="sale-void-reopen-btn">
                            <RefreshCw size={14} className="mr-2" /> Void &amp; Re-open
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </>
            ) : (
              <div className="flex items-center justify-center py-12"><div className="text-slate-400">Sale not found</div></div>
            )}
          </DialogContent>
        </Dialog>

        {/* Upload QR Dialog */}
        <UploadQRDialog
          open={uploadQROpen}
          onClose={(count) => { setUploadQROpen(false); if (count > 0) { toast.success(`${count} photo(s) uploaded`); setPhotosVersion(v => v + 1); onUpdated?.(); } }}
          recordType="invoice"
          recordId={invoice?.id}
        />
        {/* View QR Dialog */}
        <ViewQRDialog open={viewQROpen} onClose={() => setViewQROpen(false)} recordType="invoice" recordId={invoice?.id} />
        {isInvoice && invoice?.id && (
          <ReceiptQRDialog open={receiptQROpen} onClose={() => setReceiptQROpen(false)}
            docCode={invoice.doc_code || ''} invoiceId={invoice.id}
            invoiceNumber={invoice.invoice_number || invoice.sale_number || ''} docType="invoice" />
        )}
        {isInvoice && invoice && (
          <SalePaymentHistoryDialog open={payHistoryOpen} onClose={() => setPayHistoryOpen(false)}
            invoice={invoice} onOpenPP={(n) => setPpOpenNumber(n)} />
        )}
        <ProofOfPaymentDialog open={!!ppOpenNumber} onClose={() => setPpOpenNumber('')} ppNumber={ppOpenNumber}
          currentInvoiceId={invoice?.id} onOpenReceipt={hopToReceipt} />
        <PrintExtrasDialog
          open={!!printExtrasFormat}
          onOpenChange={(v) => { if (!v) setPrintExtrasFormat(null); }}
          doc={invoice}
          formatLabel={PRINT_FORMATS[printExtrasFormat] || ''}
          onConfirm={(extras) => {
            const fmt = printExtrasFormat;
            setPrintExtrasFormat(null);
            handlePrint(fmt, extras);
          }}
        />
        {renderBalanceWarnDialogs()}
        {/* Send to Print Modal — compact mode */}
        {invoice && (
          <SendToPrintModal
            open={sendToPrintOpen}
            onOpenChange={setSendToPrintOpen}
            documentType={getRemotePrintDocType()}
            receiverDocType={getReceiverDocType()}
            documentName={
              isPO ? `Purchase Order #${invoice.po_number || invoice.invoice_number || ''}` :
              isExpense ? `Expense Receipt #${invoice.invoice_number || ''}` :
              `Sales Receipt #${invoice.invoice_number || invoice.id?.slice(0,8) || ''}`
            }
            documentId={invoice.id}
            docCode={invoice.doc_code || ''}
            referenceNumber={invoice.invoice_number || invoice.po_number || ''}
            branchId={invoice.branch_id || ''}
            buildHtml={getRemotePrintHtml}
            metadata={{ invoice_id: invoice.id, customer_name: invoice.customer_name }}
          />
        )}
        {/* Send to Print — packing slip (price-free trucker copy), compact mode.
            No docCode on purpose: a doc_code render-data pull resolves back to
            the PRICED invoice type — id+type is the only safe reference. */}
        {invoice && isInvoice && (
          <SendToPrintModal
            open={packingSendOpen}
            onOpenChange={setPackingSendOpen}
            documentType="packing_slip"
            receiverDocType="packing_slip"
            documentName={`Packing Slip #${invoice.invoice_number || invoice.id?.slice(0, 8) || ''}`}
            documentId={invoice.id}
            referenceNumber={invoice.invoice_number || ''}
            branchId={invoice.branch_id || ''}
            metadata={{ invoice_id: invoice.id, customer_name: invoice.customer_name }}
          />
        )}
        {/* Verify PIN Dialog */}
        <VerifyPinDialog
          open={verifyDialogOpen}
          onClose={() => setVerifyDialogOpen(false)}
          docType="invoice"
          docId={invoice?.id}
          docLabel={saleNumber}
          onVerified={(result) => {
            setVerifyDialogOpen(false);
            setInvoice(prev => ({ ...prev, verified: true, verified_by_name: result.verified_by, verified_at: new Date().toISOString() }));
            onUpdated?.();
          }}
        />
        {/* Void dialog */}
        <Dialog open={capPinOpen} onOpenChange={(o) => { setCapPinOpen(o); if (!o) setCapPinInput(''); }}>
          <DialogContent className="sm:max-w-xs">
            <DialogHeader><DialogTitle className="text-base">Show Capital — PIN</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">Manager or admin PIN is required to view capital costs.</p>
            <Input type="password" inputMode="numeric" value={capPinInput}
              onChange={e => setCapPinInput(e.target.value)} placeholder="Enter PIN" autoFocus
              onKeyDown={e => { if (e.key === 'Enter' && capPinInput && !capBusy) fetchCapital(capPinInput); }}
              data-testid="capital-pin-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setCapPinOpen(false); setCapPinInput(''); }}>Cancel</Button>
              <Button className="flex-1" disabled={!capPinInput || capBusy} onClick={() => fetchCapital(capPinInput)}
                data-testid="capital-pin-submit">
                {capBusy ? 'Checking…' : 'Show Capital'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={voidOpen} onOpenChange={(o) => { setVoidOpen(o); if (!o) setReopenAfterVoid(false); }}>
          <DialogContent className="sm:max-w-sm" data-testid="sale-void-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                {reopenAfterVoid ? <><RefreshCw size={18} /> Void &amp; Re-open Sale</>
                  : invoice?.bir_invoice_number ? <><Ban size={18} /> Void BIR Receipt + Order Slip</>
                  : <><Ban size={18} /> Void Sale</>}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {invoice?.bir_invoice_number && (
                <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid="bir-void-note">
                  <b>BIR Receipt {invoice.bir_invoice_number} voids together with this receipt.</b>{' '}
                  The number stays in your series marked VOID — BIR does not allow
                  changes or add-ons to an issued receipt. Re-encode the corrected
                  sale afterward (use Repeat Order).
                </div>
              )}
              {reopenAfterVoid && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                  After voiding you'll be taken to a new sale draft pre-filled with these items.
                </div>
              )}
              <Input placeholder="Reason for voiding" value={voidReason} onChange={e => setVoidReason(e.target.value)} data-testid="void-reason-input" />
              <Input type="password" autoComplete="one-time-code" data-lpignore="true" placeholder="Manager/Admin PIN" value={voidPin} onChange={e => setVoidPin(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && voidReason && voidPin) handleCompactVoid(); }} data-testid="void-pin-input" />
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => { setVoidOpen(false); setVoidReason(''); setVoidPin(''); setReopenAfterVoid(false); }}>Cancel</Button>
                <Button variant="destructive" className="flex-1" onClick={handleCompactVoid} disabled={!voidReason || !voidPin || actionLoading} data-testid="void-confirm-btn">
                  {actionLoading ? 'Processing...' : (reopenAfterVoid ? 'Void & Re-open' : 'Void Sale')}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Freight Quick-Edit Dialog — must live inside compact's fragment so
            clicking "+ Add Freight" actually renders the dialog */}
        {freightEditOpen && invoice && (
          <Dialog open={freightEditOpen} onOpenChange={(o) => { if (!o) { setFreightEditOpen(false); setFreightError(''); setFreightPin(''); setFreightReason(''); } }}>
            <DialogContent className="sm:max-w-sm">
              <DialogHeader>
                <DialogTitle>{(invoice.freight || 0) > 0 ? 'Edit Freight' : 'Add Freight'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Freight Amount</Label>
                  <div className="flex items-center gap-2">
                    <span className="text-slate-400">₱</span>
                    <Input
                      type="number" min={0} step="0.01" autoFocus
                      value={freightInput}
                      onChange={e => { setFreightInput(e.target.value); setFreightError(''); }}
                      onKeyDown={e => e.key === 'Enter' && handleFreightSave()}
                      placeholder="0.00"
                      className="flex-1"
                      data-testid="freight-amount-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">Reason <span className="text-slate-400">(optional for open day)</span></Label>
                  <Input
                    value={freightReason}
                    onChange={e => setFreightReason(e.target.value)}
                    placeholder="e.g. Delivery charge confirmed"
                    data-testid="freight-reason-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500 mb-1 block">
                    Manager PIN <span className="text-slate-400">(required for closed-day invoices)</span>
                  </Label>
                  <Input
                    type="password" inputMode="numeric" maxLength={6}
                    value={freightPin}
                    onChange={e => setFreightPin(e.target.value)}
                    placeholder="PIN (if day is closed)"
                    data-testid="freight-pin-input"
                  />
                </div>
                {freightError && (
                  <p className="text-xs text-red-600 flex items-center gap-1" data-testid="freight-error">{freightError}</p>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setFreightEditOpen(false)} disabled={freightSaving}>Cancel</Button>
                <Button className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white" onClick={handleFreightSave} disabled={freightSaving} data-testid="freight-save-btn">
                  {freightSaving ? 'Saving…' : 'Save Freight'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // ══════════════════════════════════════════════════════════════════════
  // FULL MODE — tabbed layout (original InvoiceDetailModal)
  // ══════════════════════════════════════════════════════════════════════
  return (
    <>
      {/* Layer audit 2026-09-01: the compact branch demotes via modal={} while
          the QR overlays are up; this full-mode branch had only the interact
          guards — the missing half of the same fix. */}
      <Dialog open={open} onOpenChange={onOpenChange} modal={!(uploadQROpen || viewQROpen || receiptQROpen || payHistoryOpen || !!ppOpenNumber)}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col p-0 overflow-x-hidden" data-testid="invoice-detail-modal"
          onInteractOutside={(e) => hopBackInsteadOfClose(e, uploadQROpen || viewQROpen || receiptQROpen || payHistoryOpen || ppOpenNumber)}
          onEscapeKeyDown={(e) => hopBackInsteadOfClose(e, uploadQROpen || viewQROpen || receiptQROpen || payHistoryOpen || ppOpenNumber)}>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-slate-400">Loading...</div>
            </div>
          ) : invoice ? (
            <>
              {/* ── Header ───────────────────────────────────────────── */}
              <div className="px-6 py-4 border-b border-slate-100">
                <div className="flex items-start justify-between">
                  <div>
                    {hopStack.length > 0 && (
                      <button type="button" onClick={hopBack}
                        className="self-start inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
                        data-testid="sale-hop-back">
                        <ArrowLeft size={12} /> Back to {hopStack[hopStack.length - 1].pp || hopStack[hopStack.length - 1].number || 'previous receipt'}
                      </button>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-bold" data-testid="invoice-number" style={{ fontFamily: 'Manrope' }}>
                        {docNumber}
                      </h2>
                      <button onClick={copyNumber} className="text-slate-400 hover:text-slate-600" data-testid="copy-number-btn">
                        {copied ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      </button>
                      <Badge className={`text-xs ${typeInfo.color}`}>{typeInfo.label}</Badge>
                      <Badge className={`text-xs ${statusStyles[invoice.status] || 'bg-slate-100 text-slate-600'}`}>
                        {invoice.status || (isPO ? invoice.payment_status : 'N/A')}
                      </Badge>
                      {isVerified && (
                        <Badge className="text-xs bg-emerald-100 text-emerald-700">
                          <CheckCircle2 size={10} className="mr-1" /> Verified
                        </Badge>
                      )}
                      {invoice.edited && (
                        <Badge className="text-xs bg-orange-100 text-orange-700 cursor-pointer hover:bg-orange-200"
                          onClick={loadEditHistory}>
                          <Edit3 size={10} className="mr-1" /> Edited {invoice.edit_count > 1 ? `(${invoice.edit_count}x)` : ''}
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 mt-1">{docName} · {docDate}</p>
                  </div>

                  {/* Header actions */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    {isInvoice && invoice?.id && !editMode && (
                      <Button variant="outline" size="sm"
                        className="text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                        onClick={() => setReceiptQROpen(true)}
                        title="Show the customer's digital receipt as a QR to scan, or copy its link to send"
                        data-testid="receipt-qr-btn">
                        <QrCode size={14} className="mr-1" /> Receipt QR
                      </Button>
                    )}
                    {canEdit && !editMode && section === 'detail' && amendable?.allowed && (
                      <Button variant="outline" size="sm"
                        className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => { onOpenChange?.(false); navigate(`/sales-new?amend=${invoice.id}`); }}
                        title="Add items to this receipt (no BIR invoice yet, day still open)"
                        data-testid="amend-add-items-btn">
                        <Package size={14} className="mr-1" /> Add Items
                      </Button>
                    )}
                    {canEdit && !editMode && section === 'detail' && (
                      <Button variant="outline" size="sm"
                        className="text-[#1A4D2E] border-[#1A4D2E]/30 hover:bg-[#1A4D2E]/5"
                        onClick={() => {
                          // Door consolidation (owner 2026-08-31) — see the
                          // compact-layout twin above.
                          onOpenChange?.(false);
                          navigate(`/sales-new?fix=${invoice.id}`);
                        }}
                        title="Correct this receipt in the full sales grid"
                        data-testid="fix-receipt-btn">
                        <RefreshCw size={14} className="mr-1" /> Correct This Receipt
                      </Button>
                    )}
                    {canEdit && !editMode && section === 'detail' && (
                      <Button variant="outline" size="sm" onClick={() => setEditMode(true)} data-testid="edit-btn">
                        <Edit3 size={14} className="mr-1" /> Edit
                      </Button>
                    )}
                    {canVerify && (
                      <Button variant="outline" size="sm" className="text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => setVerifyOpen(true)} data-testid="verify-btn">
                        <ShieldCheck size={14} className="mr-1" /> Verify
                      </Button>
                    )}
                    {canVoid && isInvoice && (
                      <Button variant="outline" size="sm" className="text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={handleRefund} data-testid="refund-btn">
                        <RotateCcw size={14} className="mr-1" /> Refund
                      </Button>
                    )}
                    {canVoid && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => openVoidDialog(false)} data-testid="void-btn">
                        <Ban size={14} className="mr-1" /> {isPO ? 'Cancel' : 'Void'}
                      </Button>
                    )}
                    {canVoid && isInvoice && (
                      <Button variant="outline" size="sm" className="text-red-700 border-red-200 hover:bg-red-50"
                        onClick={() => openVoidDialog(true)} data-testid="void-reopen-btn">
                        <RefreshCw size={14} className="mr-1" /> Void &amp; Re-open
                      </Button>
                    )}
                    {canDeleteExpense && (
                      <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => setVoidOpen(true)} data-testid="delete-expense-btn">
                        <Ban size={14} className="mr-1" /> Delete
                      </Button>
                    )}
                  </div>
                </div>

                {/* Section tabs */}
                <div className="flex gap-1 mt-3 border-b border-slate-100 -mb-4 pb-0">
                  {[
                    { key: 'detail', label: 'Details', icon: FileText },
                    { key: 'receipts', label: 'Receipts', icon: ImageIcon },
                    ...(payments.length > 0 ? [{ key: 'payments', label: `Payments (${payments.length})`, icon: DollarSign }] : []),
                    ...(invoice.release_mode === 'partial' ? [{ key: 'releases', label: `Releases (${(invoice.stock_releases || []).length})`, icon: Package }] : []),
                    { key: 'history', label: invoice.edit_count > 0 ? `History (${invoice.edit_count} edit${invoice.edit_count === 1 ? '' : 's'})` : 'History', icon: History },
                    ...(signatures.length > 0 ? [{ key: 'signature', label: `Signature (${signatures.length})`, icon: ShieldCheck }] : [
                      { key: 'signature', label: 'Signature', icon: ShieldCheck }
                    ]),
                    ...(invoice.balance > 0 || smsItems.length > 0
                      ? [{ key: 'sms', label: smsItems.length > 0 ? `SMS (${smsItems.length})` : 'SMS', icon: MessageSquare }]
                      : []),
                  ].map(t => (
                    <button key={t.key} data-testid={`section-${t.key}`}
                      onClick={() => { setSection(t.key); if (t.key === 'history') loadEditHistory(); }}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        section === t.key ? 'border-[#1A4D2E] text-[#1A4D2E]' : 'border-transparent text-slate-400 hover:text-slate-600'
                      }`}>
                      <t.icon size={14} /> {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Content ──────────────────────────────────────────── */}
              <ScrollArea className="flex-1 px-6 py-4">

                {/* DETAIL section */}
                {section === 'detail' && (
                  <div className="space-y-5">

                    {/* ── Expense-specific view ─────────────────────── */}
                    {isExpense ? (
                      <>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <Label className="text-xs text-slate-500">Category</Label>
                            <p className="font-medium text-sm">{invoice.category || '—'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Amount</Label>
                            <p className="font-bold text-sm text-red-600">{formatPHP(invoice.amount || 0)}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Date</Label>
                            <p className="font-medium text-sm">{invoice.date || '—'}</p>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Fund Source</Label>
                            <p className="font-medium text-sm capitalize">{invoice.fund_source || 'cashier'}</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          {invoice.vendor_name && <div><Label className="text-xs text-slate-500">Vendor</Label><p className="font-medium text-sm">{invoice.vendor_name}</p></div>}
                          {invoice.reference_number && <div><Label className="text-xs text-slate-500">Reference #</Label><p className="text-sm">{invoice.reference_number}</p></div>}
                          {invoice.customer_name && <div><Label className="text-xs text-slate-500">Customer</Label><p className="font-medium text-sm">{invoice.customer_name}</p></div>}
                          {invoice.employee_name && <div><Label className="text-xs text-slate-500">Employee</Label><p className="font-medium text-sm">{invoice.employee_name}</p></div>}
                          {invoice.linked_invoice_number && <div><Label className="text-xs text-slate-500">Linked Invoice</Label><p className="text-sm">{invoice.linked_invoice_number}</p></div>}
                        </div>
                        {invoice.description && (
                          <>
                            <Separator />
                            <div>
                              <Label className="text-xs text-slate-500">Description</Label>
                              <p className="text-sm text-slate-800 mt-1">{invoice.description}</p>
                            </div>
                          </>
                        )}
                        {/* Verification info */}
                        {isVerified && (
                          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                            <CheckCircle2 size={16} className="text-emerald-600" />
                            <span className="text-sm text-emerald-700">
                              Verified by <strong>{invoice.verified_by_name}</strong> on {fmtDate(invoice.verified_at)}
                            </span>
                          </div>
                        )}
                        <div className="text-xs text-slate-400 space-y-1">
                          <p>Created: {invoice.created_at ? formatDateTime(invoice.created_at) : '—'} by {invoice.created_by_name || '—'}</p>
                        </div>
                      </>
                    ) : (
                    /* ── Invoice / PO view ──────────────────────────── */
                    <>
                    {/* Info grid */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <Label className="text-xs text-slate-500">{isPO ? 'Vendor' : 'Customer/Vendor'}</Label>
                        {editMode ? (
                          <Input value={editData.customer_name} onChange={e => setEditData({...editData, customer_name: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium text-sm">{docName}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Date</Label>
                        <p className="font-medium text-sm">{docDate}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">{isPO ? 'Due Date' : 'Due Date'}</Label>
                        <p className="font-medium text-sm">{invoice.due_date || '—'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Terms</Label>
                        <p className="font-medium text-sm">{invoice.terms || invoice.terms_label || 'COD'}</p>
                      </div>
                    </div>

                    {/* Digital payment & PO-specific info */}
                    {(invoice.digital_platform || invoice.fund_source === 'digital' || invoice.fund_source === 'split' || isPO) && (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-slate-50 rounded-lg p-3">
                        {isPO && invoice.dr_number && (
                          <div><Label className="text-xs text-slate-500">DR #</Label><p className="font-medium text-sm">{invoice.dr_number}</p></div>
                        )}
                        {isPO && (
                          <div><Label className="text-xs text-slate-500">Payment Status</Label>
                            <Badge className={`text-xs ${invoice.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-600'}`}>
                              {invoice.payment_status}
                            </Badge>
                          </div>
                        )}
                        {invoice.digital_platform && (
                          <div><Label className="text-xs text-slate-500">Platform</Label>
                            <p className="font-medium text-sm flex items-center gap-1"><Wallet size={12} className="text-blue-500" /> {invoice.digital_platform}</p></div>
                        )}
                        {invoice.digital_ref_number && (
                          <div><Label className="text-xs text-slate-500">Reference #</Label><p className="text-sm">{invoice.digital_ref_number}</p></div>
                        )}
                        {invoice.digital_sender && (
                          <div><Label className="text-xs text-slate-500">Sender</Label><p className="font-medium text-sm">{invoice.digital_sender}</p></div>
                        )}
                        {invoice.fund_source && (
                          <div><Label className="text-xs text-slate-500">Fund Source</Label><p className="font-medium text-sm capitalize">{invoice.fund_source}</p></div>
                        )}
                        {(invoice.cash_amount > 0 || invoice.digital_amount > 0) && (
                          <>
                            {invoice.cash_amount > 0 && <div><Label className="text-xs text-slate-500">Cash Portion</Label><p className="font-medium text-sm">{formatPHP(invoice.cash_amount)}</p></div>}
                            {invoice.digital_amount > 0 && <div><Label className="text-xs text-slate-500">Digital Portion</Label><p className="font-medium text-sm">{formatPHP(invoice.digital_amount)}</p></div>}
                          </>
                        )}
                      </div>
                    )}

                    {/* Verification info */}
                    {isVerified && (
                      <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                        <CheckCircle2 size={16} className="text-emerald-600" />
                        <span className="text-sm text-emerald-700">
                          Verified by <strong>{invoice.verified_by_name}</strong> on {fmtDate(invoice.verified_at)}
                          {invoice.verification_status === 'discrepancy' && <span className="text-amber-600 ml-2">(with discrepancy)</span>}
                        </span>
                      </div>
                    )}

                    <Separator />

                    {/* Items Table */}
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <Label className="text-xs text-slate-500 uppercase block">Line Items</Label>
                        {!editMode && capitalToggleBtn}
                      </div>
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-xs w-8">#</TableHead>
                              <TableHead className="text-xs">Product</TableHead>
                              <TableHead className="text-xs text-right w-20">Qty</TableHead>
                              <TableHead className="text-xs text-right w-28">Rate</TableHead>
                              <TableHead className="text-xs text-right w-24">Disc/Unit</TableHead>
                              <TableHead className="text-xs text-right w-28">Amount</TableHead>

                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(editMode ? editData.items : invoice.items || []).map((item, i) => (
                              <TableRow key={i}>
                                <TableCell className="text-xs text-slate-400">{i + 1}</TableCell>
                                <TableCell>
                                  <p className="font-medium text-sm">{item.product_name || item.description || 'Item'}</p>
                                  {item.unit && <p className="text-xs text-slate-400">{item.unit}</p>}
                                  {item.product_name ? lineDescriptionFor(item) : null}
                                  {!editMode && capitalRefsFor(item)}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className="text-sm">{item.quantity}</span>
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className="text-sm">{formatPHP(item.rate || item.unit_price || 0)}</span>
                                </TableCell>
                                <TableCell className="text-right text-sm text-emerald-600">
                                  {(() => {
                                    const dv = parseFloat(item.discount_value) || 0;
                                    const da = parseFloat(item.discount_amount) || 0;
                                    const dt = item.discount_type || 'amount';
                                    if (dv > 0) return dt === 'percent' ? `-${dv}%` : `-${formatPHP(dv)}/unit`;
                                    if (da > 0) {
                                      const qty = parseFloat(item.quantity) || 1;
                                      return `-${formatPHP(Math.round(da / qty * 100) / 100)}/unit`;
                                    }
                                    return '—';
                                  })()}
                                </TableCell>
                                <TableCell className="text-right font-medium text-sm">{formatPHP(item.total || 0)}</TableCell>

                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    </div>

                    {/* Totals */}
                    <div className="flex justify-end">
                      <div className="w-64 space-y-2">
                        <div className="flex justify-between text-sm"><span className="text-slate-500">Subtotal</span><span>{formatPHP(invoice.subtotal || invoice.line_subtotal || 0)}</span></div>
                        <div className="flex justify-between text-sm">
                          <span className="text-slate-500">Freight</span>
                          <span>{formatPHP(invoice.freight || 0)}</span>
                        </div>
                        {(invoice.overall_discount > 0 || invoice.overall_discount_amount > 0) && (
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Discount</span>
                            <span>{formatPHP(invoice.overall_discount || invoice.overall_discount_amount || 0)}</span>
                          </div>
                        )}
                        {(invoice.tax_amount || 0) > 0 && (
                          <div className="flex justify-between text-sm"><span className="text-slate-500">Tax</span><span>{formatPHP(invoice.tax_amount)}</span></div>
                        )}
                        <Separator />
                        <div className="flex justify-between font-bold"><span>Grand Total</span><span className="text-lg">{formatPHP(invoice.grand_total || 0)}</span></div>
                        {(invoice.amount_paid || 0) > 0 && (
                          <div className="flex justify-between text-sm text-emerald-600"><span>Amount Paid</span><span>{formatPHP(invoice.amount_paid)}</span></div>
                        )}
                        {(invoice.balance || 0) > 0 && (
                          <div className="flex justify-between text-sm text-red-600 font-medium"><span>Balance Due</span><span>{formatPHP(invoice.balance)}</span></div>
                        )}
                      </div>
                    </div>

                    {/* Notes */}
                    <div>
                      <Label className="text-xs text-slate-500">Notes</Label>
                      {editMode ? <Textarea value={editData.notes} onChange={e => setEditData({...editData, notes: e.target.value})} placeholder="Add notes..." rows={2} /> : <p className="text-sm text-slate-600">{invoice.notes || '—'}</p>}
                    </div>

                    {/* Edit Reason */}
                    {editMode && (
                      <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800 flex items-center justify-between gap-2 flex-wrap"
                        data-testid="edit-demoted-note-full">
                        <span>Items, freight &amp; discount are money fields — correct them with the full preview and PIN.</span>
                        <Button size="sm" variant="outline" className="h-6 text-[11px] border-emerald-300"
                          onClick={() => { onOpenChange?.(false); navigate(`/sales-new?fix=${invoice.id}`); }}>
                          Correct This Receipt
                        </Button>
                      </div>
                    )}
                    {editMode && (
                      <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-2 mb-3">
                            <AlertTriangle size={16} className="text-amber-600 mt-0.5" />
                            <div>
                              <p className="font-medium text-amber-800">Edit Reason Required</p>
                              <p className="text-xs text-amber-600">Please explain why this needs to be edited.</p>
                            </div>
                          </div>
                          <Textarea value={editReason} onChange={e => setEditReason(e.target.value)}
                            placeholder="e.g., Customer requested correction, Wrong product entered..." rows={2} className="bg-white" />
                          <div className="mt-2">
                            <Label className="text-xs text-amber-700">Proof/Attachment (optional)</Label>
                            <Input type="file" onChange={e => setProofFile(e.target.files?.[0] || null)} className="h-9 bg-white" />
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Footer info */}
                    <div className="text-xs text-slate-400 space-y-1">
                      <p>Created: {invoice.created_at ? formatDateTime(invoice.created_at) : '—'} by {invoice.cashier_name || invoice.created_by_name || '—'}</p>
                      {invoice.sales_rep_name && <p>Sales Rep: {invoice.sales_rep_name}</p>}
                      {invoice.edited && <p>Last edited: {formatDateTime(invoice.last_edited_at)} by {invoice.last_edited_by}</p>}
                    </div>
                    </>
                    )}
                  </div>
                )}

                {/* RECEIPTS section */}
                {section === 'receipts' && recordId && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm flex items-center gap-2"><ImageIcon size={16} /> Attached Receipts</h3>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" onClick={() => setUploadQROpen(true)} data-testid="upload-qr-btn">
                          <Upload size={14} className="mr-1" /> Upload via Phone
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => setViewQROpen(true)} data-testid="view-qr-btn">
                          <Smartphone size={14} className="mr-1" /> View on Phone
                        </Button>
                      </div>
                    </div>
                    <ReceiptGallery recordType={recordType} recordId={recordId} />
                  </div>
                )}

                {/* PAYMENTS section */}
                {section === 'payments' && (
                  <div className="space-y-3">
                    <h3 className="font-semibold text-sm flex items-center gap-2"><DollarSign size={16} /> Payment History</h3>
                    {payments.length === 0 ? (
                      <p className="text-slate-400 text-sm text-center py-6">No payments recorded</p>
                    ) : (
                      <div className="space-y-2">
                        {payments.map((pmt, i) => (
                          <div key={pmt.id || i} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                              <CreditCard size={14} className="text-emerald-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-sm text-emerald-700">{formatPHP(pmt.amount || 0)}</span>
                                {pmt.method && <Badge variant="outline" className="text-[10px]">{pmt.method}</Badge>}
                                {pmt.fund_source && <Badge variant="outline" className="text-[10px]">{pmt.fund_source}</Badge>}
                                {pmt.pp_number && (
                                  <button type="button" onClick={() => setPpOpenNumber(pmt.pp_number)}
                                    className="font-mono text-[11px] text-blue-700 hover:underline" title="View Proof of Payment"
                                    data-testid={`full-payment-pp-${i}`}>
                                    {pmt.pp_number}
                                  </button>
                                )}
                              </div>
                              <p className="text-xs text-slate-400 mt-0.5">
                                {pmt.date || '—'}
                                {pmt.received_by_name && ` · by ${pmt.received_by_name}`}
                                {pmt.applied_to_interest > 0 && ` · Interest: ${formatPHP(pmt.applied_to_interest)}`}
                                {pmt.applied_to_penalty > 0 && ` · Penalty: ${formatPHP(pmt.applied_to_penalty)}`}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* RELEASES section */}
                {section === 'releases' && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-sm flex items-center gap-2"><Package size={16} /> Stock Release History</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        invoice.stock_release_status === 'fully_released' ? 'bg-emerald-100 text-emerald-700' :
                        invoice.stock_release_status === 'partially_released' ? 'bg-blue-100 text-blue-700' :
                        invoice.stock_release_status === 'expired' ? 'bg-slate-200 text-slate-500' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {invoice.stock_release_status === 'fully_released' ? 'Fully Released' :
                         invoice.stock_release_status === 'partially_released' ? 'Partially Released' :
                         invoice.stock_release_status === 'expired' ? 'Expired' : 'Not Released'}
                      </span>
                    </div>
                    {invoice.doc_code && (
                      <a href={`/doc/${invoice.doc_code}`} target="_blank" rel="noreferrer"
                        className="flex items-center gap-1.5 text-xs text-blue-600 hover:underline"
                        data-testid="open-doc-page-link">
                        <Package size={12} /> Open release page (shareable link)
                      </a>
                    )}
                    {(invoice.stock_releases || []).length === 0 ? (
                      <p className="text-slate-400 text-sm text-center py-6">No releases recorded yet</p>
                    ) : (
                      <div className="space-y-3">
                        {(invoice.stock_releases || []).map((r, idx) => (
                          <div key={idx} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`release-record-${r.release_number}`}>
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-semibold text-slate-800">Release #{r.release_number}</span>
                              <span className="text-xs text-slate-400">
                                {r.released_at ? formatDateTime(r.released_at) : ''}
                              </span>
                            </div>
                            <div className="space-y-1">
                              {r.items.map((it, i) => (
                                <div key={i} className="flex justify-between text-xs">
                                  <span className="text-slate-600">{it.product_name}</span>
                                  <span className="font-semibold">{it.qty_released} {it.unit}</span>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center justify-between pt-1 border-t border-slate-100 text-xs text-slate-400">
                              <span>By {r.released_by_name} · <span className="capitalize">{(r.pin_method || '').replace('_', ' ')}</span></span>
                              <span className={r.remaining_after > 0 ? 'text-amber-600' : 'text-emerald-600'}>
                                {r.remaining_after > 0 ? `${r.remaining_after} remaining` : 'All released'}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* HISTORY section — version timeline + print trail */}
                {section === 'history' && (
                  <VersionsAndPrints invoice={invoice} editsDesc={editHistory} printHistory={printHistory} />
                )}
                {/* SIGNATURE section */}
                {section === 'signature' && (
                  <div className="space-y-3" data-testid="signature-section">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <ShieldCheck size={16} className="text-emerald-600" /> Credit Authorization Signature
                    </h3>
                    {sigLoading ? (
                      <p className="text-slate-400 text-sm text-center py-6">Loading signatures...</p>
                    ) : signatures.length === 0 ? (
                      <div className="text-center py-8 text-slate-400 space-y-2">
                        <ShieldCheck size={32} className="mx-auto text-slate-200" />
                        <p className="text-sm">No signature captured for this invoice.</p>
                        <p className="text-xs">Signatures are collected when using the Charged-to-Crop credit flow.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {signatures.map((sig, i) => (
                          <Card key={sig.id || i} className={`border-2 ${sig.status === 'signed' ? 'border-emerald-200' : sig.status === 'bypassed' ? 'border-amber-200' : 'border-slate-200'}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-[10px] ${sig.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : sig.status === 'bypassed' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {sig.status === 'signed' ? '✓ Signed' : sig.status === 'bypassed' ? 'PIN Override' : sig.status}
                                  </Badge>
                                  <span className="text-xs text-slate-400">
                                    {sig.signed_at ? formatDateTime(sig.signed_at) : sig.bypassed_at ? formatDateTime(sig.bypassed_at) : ''}
                                  </span>
                                </div>
                                {sig.status === 'bypassed' && (
                                  <span className="text-xs text-amber-600">By: {sig.bypass_by_name}</span>
                                )}
                              </div>

                              {/* Credit summary */}
                              {sig.credit_context && (
                                <div className="bg-slate-50 rounded-lg p-2.5 text-xs mb-3 grid grid-cols-2 gap-1">
                                  <div><span className="text-slate-400">Customer:</span> <span className="font-medium">{sig.credit_context.customer_name}</span></div>
                                  <div><span className="text-slate-400">Amount:</span> <span className="font-bold">{sig.credit_context.amount ? `₱${parseFloat(sig.credit_context.amount).toLocaleString('en-PH', {minimumFractionDigits: 2})}` : '—'}</span></div>
                                  <div><span className="text-slate-400">Type:</span> <span className="capitalize">{(sig.credit_context.credit_type || '').replace(/_/g, ' ')}</span></div>
                                  <div><span className="text-slate-400">Date:</span> <span>{sig.credit_context.date}</span></div>
                                </div>
                              )}

                              {/* Signature image */}
                              {sig.status === 'signed' && sig.signature_url ? (
                                <div className="border border-dashed border-slate-300 rounded-xl p-3 bg-white">
                                  <p className="text-[10px] text-slate-400 mb-2 text-center uppercase tracking-wider">Customer Signature</p>
                                  <img
                                    src={sig.signature_url}
                                    alt="Customer signature"
                                    className="max-h-32 mx-auto object-contain"
                                    onError={e => { e.target.style.display = 'none'; }}
                                  />
                                  <div className="flex justify-between items-center mt-2 pt-2 border-t border-slate-100">
                                    <div className="h-px bg-slate-300 flex-1" />
                                    <p className="text-[10px] text-slate-400 mx-2">SIGNATURE</p>
                                    <div className="h-px bg-slate-300 flex-1" />
                                  </div>
                                </div>
                              ) : sig.status === 'signed' && !sig.signature_url ? (
                                <p className="text-xs text-slate-400 text-center py-3">Signature captured but image unavailable (R2 link may have expired).</p>
                              ) : sig.status === 'bypassed' ? (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-700">
                                  <p className="font-medium">Manager PIN Override</p>
                                  <p>Reason: {sig.bypass_reason || 'Not specified'}</p>
                                  <p>Authorized by: {sig.bypass_by_name}</p>
                                </div>
                              ) : null}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* SMS section */}
                {section === 'sms' && (
                  <div className="space-y-3" data-testid="sms-section">
                    <h3 className="font-semibold text-sm flex items-center gap-2">
                      <MessageSquare size={16} className="text-sky-600" /> SMS Notifications
                    </h3>
                    {smsLoading ? (
                      <p className="text-slate-400 text-sm text-center py-6">Loading SMS history…</p>
                    ) : smsItems.length === 0 ? (
                      <div className="space-y-3">
                        <div className="text-center py-6 text-slate-400 space-y-1.5">
                          <MessageSquare size={28} className="mx-auto text-slate-200" />
                          <p className="text-sm">No SMS was queued for this invoice.</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs space-y-2" data-testid="sms-troubleshoot">
                          <p className="font-semibold text-amber-800">Common reasons SMS doesn't fire:</p>
                          <ul className="list-disc pl-5 space-y-0.5 text-amber-700">
                            <li>Customer record has <strong>no phone number</strong> on file</li>
                            <li>Trigger <code className="bg-white px-1 rounded">credit_new</code> is <strong>disabled</strong> in Settings → Messages</li>
                            <li>SMS template <code className="bg-white px-1 rounded">credit_new</code> is missing or marked inactive</li>
                            <li>This invoice was created BEFORE the SMS feature was enabled</li>
                          </ul>
                          <Button
                            variant="outline" size="sm" className="text-xs h-7 mt-1"
                            onClick={async () => {
                              try {
                                const r = await api.get('/sms/diagnose-trigger/credit_new', {
                                  params: { branch_id: invoice?.branch_id || '' }
                                });
                                setSmsDiagnosis(r.data);
                              } catch {
                                toast.error('Diagnosis failed');
                              }
                            }}
                            data-testid="run-sms-diagnosis-btn"
                          >
                            Run live diagnosis (credit_new)
                          </Button>
                          <Button
                            variant="outline" size="sm" className="text-xs h-7 mt-1 ml-2"
                            onClick={async () => {
                              try {
                                const r = await api.post('/sms/templates/backfill');
                                toast.success(r.data?.message || 'Done');
                                // re-run diagnosis
                                const dr = await api.get('/sms/diagnose-trigger/credit_new', {
                                  params: { branch_id: invoice?.branch_id || '' }
                                });
                                setSmsDiagnosis(dr.data);
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Backfill failed');
                              }
                            }}
                            data-testid="backfill-sms-templates-btn"
                          >
                            Backfill default templates
                          </Button>
                          {smsDiagnosis && (
                            <div className="mt-2 p-2 bg-white rounded border border-slate-200 space-y-1 text-[11px]">
                              {(smsDiagnosis.checks || []).map((c, i) => (
                                <div key={i} className={c.ok ? 'text-emerald-700' : 'text-red-700'}>
                                  {c.ok ? '✓' : '✗'} {c.step}: {c.detail}
                                </div>
                              ))}
                              <div className="pt-1 border-t mt-1 font-semibold">
                                Outcome: {smsDiagnosis.would_send ? '✓ Trigger would fire (template + setting OK)' : '✗ Trigger would be skipped'}
                              </div>
                              <div className="text-slate-400 italic">
                                Customer phone is checked at invoice creation time (not in this diagnosis).
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {smsItems.map((s) => (
                          <Card key={s.id} className="border border-slate-200" data-testid={`sms-item-${s.id}`}>
                            <CardContent className="p-3 space-y-2">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Badge className={`text-[10px] ${
                                    s.status === 'sent' ? 'bg-emerald-100 text-emerald-700'
                                    : s.status === 'pending' ? 'bg-sky-100 text-sky-700'
                                    : s.status === 'failed' || s.status === 'failed_permanent' ? 'bg-red-100 text-red-700'
                                    : 'bg-slate-100 text-slate-700'
                                  }`}>
                                    {s.status}
                                  </Badge>
                                  <span className="text-[11px] text-slate-500">{s.template_key}</span>
                                  <span className="text-[11px] text-slate-400">→ {s.phone}</span>
                                </div>
                                <span className="text-[10px] text-slate-400">
                                  {s.created_at ? formatDateTime(s.created_at) : ''}
                                </span>
                              </div>
                              <div className="bg-slate-50 rounded p-2 text-[11px] text-slate-700 whitespace-pre-wrap">
                                {s.message || ''}
                              </div>
                              {s.error && (
                                <p className="text-[11px] text-red-600">Error: {s.error}</p>
                              )}
                              {s.sent_at && (
                                <p className="text-[10px] text-emerald-700">Sent {formatDateTime(s.sent_at)}</p>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

              </ScrollArea>

              {/* ── Footer (edit mode) ───────────────────────────────── */}
              {editMode && section === 'detail' && (
                <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => { setEditMode(false); setEditReason(''); loadInvoice(); }}>Cancel</Button>
                  <Button onClick={handleSave} disabled={saving || !editReason.trim()} className="bg-[#1A4D2E] hover:bg-[#14532d] text-white">
                    {saving ? 'Saving...' : <><Save size={14} className="mr-2" /> Save Changes</>}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center py-20"><div className="text-slate-400">Not found</div></div>
          )}
        </DialogContent>
      </Dialog>

      {/* Verify PIN dialog */}
      <Dialog open={verifyOpen} onOpenChange={setVerifyOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="verify-pin-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ShieldCheck size={18} className="text-emerald-600" /> Verify Transaction</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-500">Enter admin/manager PIN to verify this transaction</p>
            <Input data-testid="verify-pin-input" type="password" autoComplete="one-time-code" data-lpignore="true"
              placeholder="Enter PIN" value={verifyPin}
              onChange={e => setVerifyPin(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') handleVerify(); }} />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setVerifyOpen(false); setVerifyPin(''); }}>Cancel</Button>
              <Button className="flex-1 bg-emerald-600 hover:bg-emerald-700" onClick={handleVerify} disabled={!verifyPin || actionLoading} data-testid="verify-confirm-btn">
                {actionLoading ? 'Verifying...' : 'Verify'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={capPinOpen} onOpenChange={(o) => { setCapPinOpen(o); if (!o) setCapPinInput(''); }}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader><DialogTitle className="text-base">Show Capital — PIN</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">Manager or admin PIN is required to view capital costs.</p>
          <Input type="password" inputMode="numeric" value={capPinInput}
            onChange={e => setCapPinInput(e.target.value)} placeholder="Enter PIN" autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && capPinInput && !capBusy) fetchCapital(capPinInput); }}
            data-testid="capital-pin-input" />
          <div className="flex gap-2">
            <Button variant="outline" className="flex-1" onClick={() => { setCapPinOpen(false); setCapPinInput(''); }}>Cancel</Button>
            <Button className="flex-1" disabled={!capPinInput || capBusy} onClick={() => fetchCapital(capPinInput)}
              data-testid="capital-pin-submit">
              {capBusy ? 'Checking…' : 'Show Capital'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Void dialog */}
      <Dialog open={voidOpen} onOpenChange={(o) => { setVoidOpen(o); if (!o) setReopenAfterVoid(false); }}>
        <DialogContent className="sm:max-w-sm" data-testid="void-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-600">
            {reopenAfterVoid ? <><RefreshCw size={18} /> Void &amp; Re-open Invoice</>
              : (isInvoice && invoice?.bir_invoice_number) ? <><Ban size={18} /> Void BIR Receipt + Order Slip</>
              : <><Ban size={18} /> {isExpense ? 'Delete Expense' : isPO ? 'Cancel PO' : 'Void Invoice'}</>}
          </DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
                        {invoice?.bir_invoice_number && (
              <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-800" data-testid="bir-void-note">
                <b>BIR Receipt {invoice.bir_invoice_number} voids together with this receipt.</b>{' '}
                The number stays in your series marked VOID — BIR does not allow
                changes or add-ons to an issued receipt. Re-encode the corrected
                sale afterward (use Repeat Order).
              </div>
            )}
{reopenAfterVoid && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                After voiding you'll be taken to a new sale draft pre-filled with these items.
              </div>
            )}
            <Input placeholder="Reason" value={voidReason} onChange={e => setVoidReason(e.target.value)} data-testid="void-reason-input" />
            <Input type="password" autoComplete="one-time-code" data-lpignore="true"
              placeholder="Manager/Admin PIN" value={voidPin} onChange={e => setVoidPin(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && voidReason && voidPin) handleVoid(); }} data-testid="void-pin-input" />
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => { setVoidOpen(false); setVoidReason(''); setVoidPin(''); setReopenAfterVoid(false); }}>Cancel</Button>
              <Button variant="destructive" className="flex-1" onClick={handleVoid} disabled={!voidReason || !voidPin || actionLoading} data-testid="void-confirm-btn">
                {actionLoading ? 'Processing...' : (reopenAfterVoid ? 'Void & Re-open' : isExpense ? 'Delete' : isPO ? 'Cancel PO' : 'Void')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QR Upload dialog */}
      <UploadQRDialog
        open={uploadQROpen}
        onClose={(count) => { setUploadQROpen(false); if (count > 0) { toast.success(`${count} photo(s) uploaded`); } }}
        recordType={recordType}
        recordId={recordId}
      />

      {isInvoice && invoice?.id && (
        <ReceiptQRDialog open={receiptQROpen} onClose={() => setReceiptQROpen(false)}
          docCode={invoice.doc_code || ''} invoiceId={invoice.id}
          invoiceNumber={invoice.invoice_number || invoice.sale_number || ''} docType="invoice" />
      )}
      <ProofOfPaymentDialog open={!!ppOpenNumber} onClose={() => setPpOpenNumber('')} ppNumber={ppOpenNumber}
        currentInvoiceId={invoice?.id} onOpenReceipt={hopToReceipt} />

      {/* QR View dialog */}
      <ViewQRDialog
        open={viewQROpen}
        onClose={() => setViewQROpen(false)}
        recordType={recordType}
        recordId={recordId}
      />
      {renderBalanceWarnDialogs()}

      {/* Freight Quick-Edit Dialog */}
      {freightEditOpen && invoice && (
        <Dialog open={freightEditOpen} onOpenChange={(o) => { if (!o) { setFreightEditOpen(false); setFreightError(''); setFreightPin(''); setFreightReason(''); } }}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>{(invoice.freight || 0) > 0 ? 'Edit Freight' : 'Add Freight'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Freight Amount</Label>
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">₱</span>
                  <Input
                    type="number" min={0} step="0.01" autoFocus
                    value={freightInput}
                    onChange={e => { setFreightInput(e.target.value); setFreightError(''); }}
                    onKeyDown={e => e.key === 'Enter' && handleFreightSave()}
                    placeholder="0.00"
                    className="flex-1"
                    data-testid="freight-amount-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">Reason <span className="text-slate-400">(optional for open day)</span></Label>
                <Input
                  value={freightReason}
                  onChange={e => setFreightReason(e.target.value)}
                  placeholder="e.g. Delivery charge confirmed"
                  data-testid="freight-reason-input"
                />
              </div>
              {/* Manager PIN — shown always; required if day is closed */}
              <div>
                <Label className="text-xs text-slate-500 mb-1 block">
                  Manager PIN <span className="text-slate-400">(required for closed-day invoices)</span>
                </Label>
                <Input
                  type="password" inputMode="numeric" maxLength={6}
                  value={freightPin}
                  onChange={e => setFreightPin(e.target.value)}
                  placeholder="PIN (if day is closed)"
                  data-testid="freight-pin-input"
                />
              </div>
              {freightError && (
                <p className="text-xs text-red-600 flex items-center gap-1" data-testid="freight-error">{freightError}</p>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" className="flex-1" onClick={() => setFreightEditOpen(false)} disabled={freightSaving}>Cancel</Button>
              <Button className="flex-1 bg-[#1A4D2E] hover:bg-[#14532d] text-white" onClick={handleFreightSave} disabled={freightSaving} data-testid="freight-save-btn">
                {freightSaving ? 'Saving…' : 'Save Freight'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Send to Print Modal */}
      {invoice && (
        <SendToPrintModal
          open={sendToPrintOpen}
          onOpenChange={setSendToPrintOpen}
          documentType={getRemotePrintDocType()}
          receiverDocType={getReceiverDocType()}
          documentName={
            isPO ? `Purchase Order #${invoice.po_number || invoice.invoice_number || ''}` :
            isExpense ? `Expense Receipt #${invoice.invoice_number || ''}` :
            `Sales Receipt #${invoice.invoice_number || invoice.id?.slice(0,8) || ''}`
          }
          documentId={invoice.id}
          docCode={invoice.doc_code || ''}
          referenceNumber={invoice.invoice_number || invoice.po_number || ''}
          branchId={invoice.branch_id || ''}
          buildHtml={getRemotePrintHtml}
          metadata={{ invoice_id: invoice.id, customer_name: invoice.customer_name }}
        />
      )}
    </>
  );
}
